import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './WaiterDashboard.css';
import './OwnerDashboard.css';
import './OrderHistoryStyles.css';
import { Menu as MenuIcon, Wifi, WifiOff, Clock, DollarSign, Users, X, ShoppingBag, Bold, Filter, Bell, Home, Volume2, VolumeX } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowLeft, faUtensils, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import qrBillingService from '../../services/qrBillingService';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useBackupPolling } from '../../hooks/useBackupPolling';
import BusinessVideoUpload from './BusinessVideoUpload';
import FloorManagement from './FloorManagement';
import RatingsDashboard from './RatingsDashboard';
import OwnerNotifications from './OwnerNotifications';
import customVoiceService from '../../utils/customVoice';

// Dynamic status generator with real-time color mapping
const getTableStatus = (table, confirmedPaymentTimestamps) => {
  if (!table) return { semantic: 'ash', status: 'Available', color: '#4D5469' };

  // ✅ PRIORITY 1: Check if payment was confirmed - handle both active and expired cases
  if (confirmedPaymentTimestamps && confirmedPaymentTimestamps[table.tableNumber]) {
    const confirmData = confirmedPaymentTimestamps[table.tableNumber];
    const timeSinceConfirmation = Date.now() - confirmData.timestamp;
    if (timeSinceConfirmation < 120000) { // Less than 2 minutes - GREEN
      return {
        semantic: 'green',
        status: 'Paid Successfully',
        color: '#25C35E',
        icon: 'check'
      };
    } else {
      // More than 2 minutes - ASH (override backend semantic)
      return {
        semantic: 'ash',
        status: 'Available',
        color: '#4D5469',
        icon: 'empty'
      };
    }
  }

  // ✅ PRIORITY 2: If backend says green, honor it even if local timestamp is missing
  if (table.semantic === 'green') {
    return {
      semantic: 'green',
      status: table.statusText || table.status || 'Paid Successfully',
      color: '#25C35E',
      icon: 'check'
    };
  }

  // Check for stale yellow tables
  if (table.semantic === 'yellow') {
    const timeInHours = table.timeMs / (1000 * 60 * 60);
    if (timeInHours > 24) {
      return {
        semantic: 'ash',
        status: 'Stale',
        color: '#4D5469',
        icon: 'clock'
      };
    }
  }

  // Use statusText from backend if available, otherwise use semantic-based defaults
  const statusText = table.statusText || table.status || 'Available';

  // Real-time semantic color mapping (for yellow, red, ash)
  switch (table.semantic) {
    case 'yellow':
      return {
        semantic: 'yellow',
        status: statusText, // Use dynamic status text from backend
        color: '#EAB308',
        icon: 'clock'
      };
    case 'red':
      return {
        semantic: 'red',
        status: 'Needs Attention',
        color: '#ef4444',
        icon: 'alert'
      };
    default:
      return {
        semantic: 'ash',
        status: 'Available',
        color: '#4D5469',
        icon: 'empty'
      };
  }
};

const getApiBase = () => {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  try {
    const url = new URL(base);
    const host = window?.location?.hostname;
    if (host && (url.hostname === 'localhost' || url.hostname === '127.0.0.1')) {
      url.hostname = host;
    }
    return url.toString().replace(/\/+$/, '');
  } catch {
    return base.replace(/\/+$/, '');
  }
};

// Real-time notification component
const RealTimeNotification = ({ notification, onClose }) => (
  <div className={`rt-notification rt-${notification.type}`}>
    <div className="rt-notification-content">
      <strong>{notification.title}</strong>
      <span>{notification.message}</span>
    </div>
    <button onClick={onClose} className="rt-notification-close">×</button>
  </div>
);

// Dynamic table card with WebSocket updates
const DynamicTableCard = ({ table, onTableClick, isFlashing, hasCashAnimation, confirmedPaymentTimestamps }) => {
  const status = getTableStatus(table, confirmedPaymentTimestamps);
  const amount = table?.amount || 0;
  const time = table?.time && table.time !== '0m' ? table.time : '';
  const isPaid = status.semantic === 'green';
  const isOccupied = status.semantic === 'yellow';
  const isLightStatus = isPaid || isOccupied; // both should be regular weight and left aligned

  // Dynamic style based on real-time state
  const cardStyle = {
    backgroundColor: hasCashAnimation ? '#FF8D28' : (isFlashing ? '#ef4444' : status.color), // Orange for payment received
    transform: 'scale(1)',
    animation: 'none',
    transition: 'all 0.3s ease-in-out',
    boxShadow: hasCashAnimation
      ? '0 4px 6px rgba(0, 0, 0, 0.1)' // No glow for payment received
      : (isFlashing
        ? '0 0 20px rgba(239, 68, 68, 0.5)'
        : '0 4px 6px rgba(0, 0, 0, 0.1)'),
  };

  return (
    <div
      className="rt-table-card"
      style={cardStyle}
      onClick={() => onTableClick(table)}
    >
      {status.semantic === 'ash' ? (
        <div className="rt-table-empty">
          <div className="rt-table-number">{table.name}</div>
        </div>
      ) : (
        <div className="rt-table-content">
          <div className="rt-table-header">
            <div className="rt-table-number">{table.name}</div>
            <div className={`rt-table-icon ${status.icon === 'check' ? 'rt-table-icon--paid' : ''}`}>
              {hasCashAnimation ? <CashIcon /> : (
                <>
                  {status.icon === 'check' && <CheckIcon bold />}
                  {status.icon === 'clock' && <ClockIcon />}
                  {status.icon === 'alert' && <AlertIcon />}
                </>
              )}
            </div>
          </div>
          <div className={`rt-table-status ${isLightStatus ? 'rt-table-status--light' : ''}`}>
            {hasCashAnimation ? 'Payment Received' : status.status}
          </div>
          <div className="rt-table-footer">
            <div className="rt-table-amount">₹{Number(amount).toFixed(0)}</div>
            {time && <div className="rt-table-time">{time}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

// Icon components
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12,6 12,12 16,14" />
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const CashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="7" width="20" height="10" rx="2" />
    <circle cx="12" cy="12" r="3" />
    <path d="M18 12h.01M6 12h.01" />
  </svg>
);

// Real-time stats dashboard - Main Counter Card
const RealTimeStats = ({ tables, totalRevenue, onClick }) => {
  return (
    <div className="rt-stats-container">
      <div className="rt-main-counter-card" onClick={onClick} style={{ cursor: 'pointer' }}>
        <div className="rt-counter-left">
          <div className="rt-counter-label">Main Counter</div>
          <div className="rt-counter-subtitle">Total Revenue</div>
        </div>
        <div className="rt-counter-value">₹{Number(totalRevenue).toFixed(0)}</div>
      </div>
    </div>
  );
};

export default function OwnerDashboard({ goTo, onModalChange, onNavigateToMenu, onNavigateToCoreDashboard, onNavigateToTables }) {
  // Core state for real-time dashboard
  const [tables, setTables] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Real-time features
  const [notifications, setNotifications] = useState([]);
  const [flashingTables, setFlashingTables] = useState(new Set());
  // Store pending payments with full order data
  const [pendingPayments, setPendingPayments] = useState(new Map()); // tableNumber -> orderDetails
  const [showPaymentConfirmModal, setShowPaymentConfirmModal] = useState(false);
  const [pendingPaymentOrder, setPendingPaymentOrder] = useState(null);
  const [confirmedPaymentTimestamps, setConfirmedPaymentTimestamps] = useState({}); // { tableNumber: { timestamp, sessionId } }

  // Timer state for live updates
  const [nowMs, setNowMs] = useState(Date.now());

  // Table details modal state
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableDetails, setTableDetails] = useState(null);
  const [tableDetailsFetchedAt, setTableDetailsFetchedAt] = useState(null); // Track when details were fetched
  const [loadingTableDetails, setLoadingTableDetails] = useState(false);
  const [showAllOrdersModal, setShowAllOrdersModal] = useState(false);
  const [showOrderHistoryPage, setShowOrderHistoryPage] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [orderHistoryTable, setOrderHistoryTable] = useState(null);

  // Waiter management state
  const [showWaiterModal, setShowWaiterModal] = useState(false);
  const [waiterCount, setWaiterCount] = useState(1);
  const [waiterAssignments, setWaiterAssignments] = useState([]);
  const [totalTables, setTotalTables] = useState(0);
  const [editableTotalTables, setEditableTotalTables] = useState(0);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  // Modal tab state for Waiter Management modal: 'Waiters' | 'Floors'
  const [waiterModalActiveTab, setWaiterModalActiveTab] = useState('Waiters');

  // Status modal (replaces window.alert)
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: 'info',
    message: ''
  });
  const statusModalTimerRef = useRef(null);
  const voiceAnnouncementIntervalRef = useRef(null);

  // Voice selector state
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [testingVoice, setTestingVoice] = useState(null);

  const showStatusModal = (message, type = 'error', autoCloseMs = 2500) => {
    if (statusModalTimerRef.current) {
      clearTimeout(statusModalTimerRef.current);
      statusModalTimerRef.current = null;
    }

    setStatusModal({
      open: true,
      type,
      message: String(message || '')
    });

    if (autoCloseMs && autoCloseMs > 0) {
      statusModalTimerRef.current = setTimeout(() => {
        setStatusModal(prev => (prev.open ? { ...prev, open: false } : prev));
        statusModalTimerRef.current = null;
      }, autoCloseMs);
    }
  };

  // Power Menu state
  const [showPowerMenuModal, setShowPowerMenuModal] = useState(false);
  const [menuMode, setMenuMode] = useState('dining'); // 'takeaway' or 'dining' (removed 'both')
  const [loadingMenuMode, setLoadingMenuMode] = useState(false);

  // Auto-open Power Menu when returning from external menu app
  useEffect(() => {
    try {
      const shouldOpen = sessionStorage.getItem('invexis:open_power_menu') === 'true';
      if (shouldOpen) {
        sessionStorage.removeItem('invexis:open_power_menu');
        setShowPowerMenuModal(true);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Dropdown menu state
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);

  // Dashboard toggles state
  const [waiterDashboardEnabled, setWaiterDashboardEnabled] = useState(false);
  const [chefDashboardEnabled, setChefDashboardEnabled] = useState(false);
  
  // Warning modal state for toggle off
  const [showToggleWarningModal, setShowToggleWarningModal] = useState(false);
  const [pendingToggleChange, setPendingToggleChange] = useState(null); // { type: 'waiter' | 'chef', newState: boolean }

  // Voice-over state for owner dashboard
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    const saved = localStorage.getItem('ownerVoiceEnabled');
    return saved !== null ? saved === 'true' : true; // Default enabled
  });
  const voiceEnabledRef = useRef(voiceEnabled);
  
  // Voice selection state
  const [selectedVoiceName, setSelectedVoiceName] = useState(() => {
    return localStorage.getItem('ownerSelectedVoice') || null;
  });
  const selectedVoiceRef = useRef(selectedVoiceName);
  
  useEffect(() => {
    try {
      const userStr = window.localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setUserId(user.user_id);
        setBusinessId(user.business_id);
      }
    } catch (e) {
      console.error('Failed to check user role:', e);
    }
  }, []);

  useEffect(() => {
    voiceEnabledRef.current = voiceEnabled;
    selectedVoiceRef.current = selectedVoiceName;
  }, [voiceEnabled, selectedVoiceName]);

  useEffect(() => {
    return () => {
      if (statusModalTimerRef.current) {
        clearTimeout(statusModalTimerRef.current);
        statusModalTimerRef.current = null;
      }
    };
  }, []);

  // Load available voices when component mounts
  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis?.getVoices() || [];
      if (voices.length > 0) {
        setAvailableVoices(voices);
        
        // Initialize custom voice service with saved voice preference
        const savedVoice = localStorage.getItem('ownerSelectedVoice');
        if (savedVoice) {
          customVoiceService.updateVoiceSettings({ voiceName: savedVoice });
        }
      } else {
        // Try again after a delay
        setTimeout(loadVoices, 100);
      }
    };

    // Try to load immediately
    loadVoices();
    
    // Also listen for voiceschanged event
    if (window.speechSynthesis) {
      window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    }
    
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
      }
    };
  }, []);

  // Initialize speech synthesis on first user interaction (browser requirement)
  useEffect(() => {
    const initializeSpeech = () => {
      if (window.speechSynthesis) {
        // Trigger speech synthesis to unlock it (browser security requirement)
        const utterance = new SpeechSynthesisUtterance('');
        utterance.volume = 0;
        window.speechSynthesis.speak(utterance);
      }
      // Remove listeners after first interaction
      document.removeEventListener('click', initializeSpeech);
      document.removeEventListener('touchstart', initializeSpeech);
    };
    
    // Add listeners for first user interaction
    document.addEventListener('click', initializeSpeech, { once: true });
    document.addEventListener('touchstart', initializeSpeech, { once: true });
    
    return () => {
      document.removeEventListener('click', initializeSpeech);
      document.removeEventListener('touchstart', initializeSpeech);
    };
  }, []);

  // Notifications state
  const [showNotifications, setShowNotifications] = useState(false);
  // Floor filter dropdown state
  const [showFloorMenu, setShowFloorMenu] = useState(false);
  const floorMenuRef = useRef(null);

   // Load total tables from API
  useEffect(() => {
    const loadTotalTables = async () => {
      if (!businessId) return;

      try {
        const API_BASE = getApiBase();
        const response = await fetch(`${API_BASE}/qr-billing/dashboard`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Business-ID': businessId
          }
        });

        if (response.ok) {
          const result = await response.json();
          const tables = result.data?.tables || result.tables || [];
          
          // Count only regular tables (exclude OWNER and TAKEAWAY)
          const regularTables = tables.filter(table => 
            table.tableNumber !== 'OWNER' && 
            table.tableNumber !== 'TAKEAWAY'
          );
          
          setTotalTables(regularTables.length);
        }
      } catch (err) {
        console.error('Error loading total tables:', err);
      }
    };

    loadTotalTables();
  }, [businessId]);

  // Timer to force re-render when 2-minute green period expires
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const hasExpiredPayments = Object.entries(confirmedPaymentTimestamps).some(([_, data]) => {
        return now - data.timestamp >= 120000;
      });
      
      if (hasExpiredPayments) {
        // Trigger re-render by updating state
        setConfirmedPaymentTimestamps(prev => ({ ...prev }));
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, [confirmedPaymentTimestamps]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!floorMenuRef.current) return;
      if (!floorMenuRef.current.contains(e.target)) setShowFloorMenu(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setShowFloorMenu(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // Take Away Orders state
  const [showTakeAwayOrdersModal, setShowTakeAwayOrdersModal] = useState(false);
  const [takeAwayOrders, setTakeAwayOrders] = useState([]);
  const [loadingTakeAwayOrders, setLoadingTakeAwayOrders] = useState(false);

  // Video Upload state
  const [showVideoUploadModal, setShowVideoUploadModal] = useState(false);

  // Google Maps Link state
  const [showGoogleMapsModal, setShowGoogleMapsModal] = useState(false);
  const [googleMapsLink, setGoogleMapsLink] = useState('');
  const [googleMapsLinkInput, setGoogleMapsLinkInput] = useState('');
  const [loadingGoogleMapsLink, setLoadingGoogleMapsLink] = useState(false);
  const [googleMapsLinkError, setGoogleMapsLinkError] = useState('');

  // Merchant UPI Link state
  const [showMerchantUpiModal, setShowMerchantUpiModal] = useState(false);
  const [merchantUpiLink, setMerchantUpiLink] = useState('');
  const [merchantUpiLinkInput, setMerchantUpiLinkInput] = useState('');
  const [loadingMerchantUpiLink, setLoadingMerchantUpiLink] = useState(false);
  const [merchantUpiLinkError, setMerchantUpiLinkError] = useState('');
  const [upiId, setUpiId] = useState('');
  const [merchantName, setMerchantName] = useState('');
  
  // Payment QR Code Upload state
  const [showQrUploadModal, setShowQrUploadModal] = useState(false);
  const [paymentQrImage, setPaymentQrImage] = useState(null);
  const [paymentQrPreview, setPaymentQrPreview] = useState(null);
  const [loadingQrUpload, setLoadingQrUpload] = useState(false);
  const [qrUploadError, setQrUploadError] = useState('');
  const [useManualLink, setUseManualLink] = useState(false);

  // Active screen state for navigation
  const [activeScreen, setActiveScreen] = useState('dashboard');

  // Floor filter state
  const [selectedFloor, setSelectedFloor] = useState('all');
  const [availableFloors, setAvailableFloors] = useState([]);

  // Load all available floors (independent of filter)
  const loadAvailableFloors = useCallback(async () => {
    if (!businessId) return;

    try {
      const API_BASE = getApiBase();
      const response = await fetch(`${API_BASE}/qr-billing/floors`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Business-ID': businessId
        }
      });

      if (response.ok) {
        const result = await response.json();
        const floors = result.floors.map(f => f.floor).filter(Boolean);
        setAvailableFloors(floors);
      }
    } catch (error) {
      console.error('❌ Error loading floors:', error);
    }
  }, [businessId]);

  // Initialize business data
  useEffect(() => {
    const storedBusinessId = localStorage.getItem('businessId');
    const storedUserId = localStorage.getItem('userId');

    if (storedBusinessId) setBusinessId(parseInt(storedBusinessId));
    if (storedUserId) setUserId(parseInt(storedUserId));

    // Load waiter assignments from backend API (with localStorage fallback)
    const loadWaiterAssignments = async () => {
      if (!storedBusinessId) return;

      try {
        const API_BASE = getApiBase();
        const response = await fetch(`${API_BASE}/waiter-assignments/${storedBusinessId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Business-ID': storedBusinessId
          }
        });

        if (response.ok) {
          const result = await response.json();

          // Always update totalTables if provided by backend
          if (result.totalTables !== undefined && result.totalTables !== null) {
            setTotalTables(result.totalTables);
            setEditableTotalTables(result.totalTables);
            localStorage.setItem(`totalTables_${storedBusinessId}`, result.totalTables.toString());
          }

          // Update assignments if they exist
          if (result.success && result.assignments && result.assignments.length > 0) {
            const tableCount = Number(result.totalTables || 0);
            const hasOutOfRange = tableCount > 0 && result.assignments.some(a =>
              Number(a.fromTable) < 1 || Number(a.toTable) > tableCount
            );

            const normalized = hasOutOfRange
              ? buildEvenAssignments(result.assignments.length, tableCount, result.assignments)
              : result.assignments;

            setWaiterAssignments(normalized);
            setWaiterCount(normalized.length);

            // Update localStorage as cache
            localStorage.setItem(`waiterAssignments_${storedBusinessId}`, JSON.stringify(normalized));
            return;
          }
        }
      } catch (error) {
        console.warn('⚠️ Failed to load from backend, using localStorage:', error);
      }

      // Fallback to localStorage if backend fails
      const savedAssignments = localStorage.getItem(`waiterAssignments_${storedBusinessId}`);
      if (savedAssignments) {
        const assignments = JSON.parse(savedAssignments);
        const cachedTotalTables = Number(localStorage.getItem(`totalTables_${storedBusinessId}`) || 0);
        const hasOutOfRange = cachedTotalTables > 0 && assignments.some(a =>
          Number(a.fromTable) < 1 || Number(a.toTable) > cachedTotalTables
        );

        const normalized = hasOutOfRange
          ? buildEvenAssignments(assignments.length, cachedTotalTables, assignments)
          : assignments;

        setWaiterAssignments(normalized);
        setWaiterCount(normalized.length);
      }
    };

    loadWaiterAssignments();

    // Load dashboard toggle states
    const loadToggles = async () => {
      if (!storedBusinessId) return;

      try {
        const API_BASE = getApiBase();
        const response = await fetch(`${API_BASE}/qr-billing/dashboard-toggles?businessId=${storedBusinessId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Business-ID': storedBusinessId
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.toggles) {
            setWaiterDashboardEnabled(result.toggles.waiterEnabled || false);
            setChefDashboardEnabled(result.toggles.chefEnabled || false);
          }
        }
      } catch (error) {
        console.warn('⚠️ Failed to load dashboard toggles:', error);
      }
    };

    loadToggles();

    // Request notification permissions for real-time alerts
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Update timer every second for live time display
  useEffect(() => {
    const interval = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Real-time notification management
  const addNotification = useCallback((notification) => {
    const newNotification = {
      ...notification,
      id: Date.now(),
      timestamp: new Date()
    };

    setNotifications(prev => [newNotification, ...prev].slice(0, 5));

    // Auto-remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== newNotification.id));
    }, 5000);
  }, []);

  // Voice synthesis for order announcements using Custom Voice Service
  const speakOrderAnnouncement = useCallback(async (tableNumber, items) => {
    const shouldSpeak = voiceEnabledRef.current;
    
    if (!shouldSpeak) {
      return;
    }

    // Build announcement text
    let announcement = `New order from table ${tableNumber}. `;
    
    if (items && items.length > 0) {
      items.forEach(item => {
        const itemName = item.menu_item_name || item.itemName || item.name || item.item_name || 'item';
        const quantity = item.quantity || item.current_quantity || item.qty || 1;
        announcement += `${quantity} ${itemName}. `;
      });
    } else {
      announcement += 'Please check the screen.';
    }

    try {
      // ✅ Use Custom Voice Service (handles Balakrishna voice if configured)
      await customVoiceService.speak(announcement, {
        voiceName: selectedVoiceRef.current // Use user-selected voice if available
      });
    } catch (error) {
      console.error('❌ Voice announcement error:', error);
    }
  }, []);

  // Toggle voice feature
  const toggleVoice = useCallback(() => {
    setVoiceEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('ownerVoiceEnabled', newValue.toString());
      if (!newValue) {
        // Stop all announcements using custom voice service
        customVoiceService.stop();
      } else {
        setTimeout(() => {
          if (voiceEnabledRef.current) {
            customVoiceService.speak('Voice enabled', {
              voiceName: selectedVoiceRef.current
            }).catch(err => console.error('❌ Test voice error:', err));
          }
        }, 300);
      }
      
      return newValue;
    });
  }, []);

  // Test voice with sample sentence
  const testVoiceWithSample = useCallback(async (voiceName) => {
    setTestingVoice(voiceName);
    const sampleSentence = "New order from table 5. Two Chicken Biryani and one Coke. Please confirm payment for table 3. Amount rupees 450.";
    
    try {
      await customVoiceService.speak(sampleSentence, {
        voiceName: voiceName
      });
    } catch (error) {
      console.error('❌ Voice test error:', error);
    } finally {
      setTestingVoice(null);
    }
  }, []);

  // Select and save voice
  const selectVoice = useCallback((voiceName) => {
    setSelectedVoiceName(voiceName);
    localStorage.setItem('ownerSelectedVoice', voiceName);
    customVoiceService.updateVoiceSettings({ voiceName });
  }, []);

  // Reload voices manually
  const reloadVoices = useCallback(() => {
    const voices = window.speechSynthesis?.getVoices() || [];
    setAvailableVoices(voices);
  }, []);


  // Handle real-time dashboard updates
  const handleDashboardUpdate = useCallback((data) => {
    const tablesData = data.data?.tables || data.tables;
    const revenueData = data.data?.totalRevenue || data.totalRevenue;

    if (tablesData) {
      // 🔥 DEDUPLICATE tables by tableNumber - keep the latest entry
      const tableMap = new Map();
      tablesData.forEach(t => {
        const tableNum = t.tableNumber || t.table;
        // If duplicate exists, keep the one with more recent data (higher amount or newer timestamp)
        if (!tableMap.has(tableNum) ||
          (t.amount > tableMap.get(tableNum).amount) ||
          (t.timeMs > tableMap.get(tableNum).timeMs)) {
          tableMap.set(tableNum, t);
        }
      });

      // 🔥 CRITICAL: Force new array reference to trigger React re-render
      const freshTables = Array.from(tableMap.values()).map(t => ({
        ...t,
        _updateTimestamp: Date.now() // Force unique reference
      }));

      setTables(freshTables);
      setTotalRevenue(revenueData || 0);
      setLastUpdated(new Date());
      setError(null);
      setLoading(false);
    }
  }, []);

  // Handle new order notifications
  const handleNewOrder = useCallback((data) => {
    addNotification({
      type: 'order',
      title: ' New Order',
      message: `Table ${data.tableNumber} - ₹${data.total}`,
      priority: 'normal'
    });
  }, [addNotification]);

  // Handle payment notifications
  const handlePaymentCompleted = useCallback((data) => {
    addNotification({
      type: 'payment',
      title: '💳 Payment Received',
      message: `Table ${data.tableNumber} - ₹${data.total}`,
      priority: 'normal'
    });
  }, [addNotification]);

  // WebSocket connection for real-time updates
  const { isConnected, connectionError } = useWebSocket({
    businessId,
    userId,
    onDashboardUpdate: handleDashboardUpdate,
    onNewOrder: handleNewOrder,
    onPaymentCompleted: handlePaymentCompleted,
    onPaymentConfirmed: (data) => {
      // Set timestamp for green → ash transition
      if (data.tableNumber) {
        const tableNum = String(data.tableNumber);
        setConfirmedPaymentTimestamps(prev => ({
          ...prev,
          [tableNum]: { 
            timestamp: Date.now(), 
            sessionId: data.sessionId || null 
          }
        }));
      }
      
      // 🔄 CRITICAL: Immediately refresh dashboard to show GREEN table status
      loadDashboard(false);
    },
    onOrderStatusChanged: () => { }, // Simplified handler
    onNotification: () => { }, // Simplified handler
    onWaiterCall: null, // Owner dashboard should NOT receive waiter calls - only waiter dashboard should
    onOrderConfirmed: (data) => {
      // Immediately refresh dashboard to show table turning green
      loadDashboard(false);
    },
    onCounterPaymentRequest: async (data) => {

      if (!data.tableNumber || !data.orderId) {
        console.error('❌ Missing required data in counter payment request');
        return;
      }

      // Prepare pending payment order data - items are already included in the event
      const pendingOrder = {
        orderId: data.orderId,
        tableNumber: data.tableNumber,
        amount: data.amount,
        items: data.items || [],
        orderCount: data.orderCount || 1,
        paymentMethod: data.paymentMethod || 'Pay at Counter'
      };

      // Show the confirmation modal
      setPendingPaymentOrder(pendingOrder);
      setShowPaymentConfirmModal(true);

      // Add to pending payments map (makes table orange)
      const tableNumStr = String(data.tableNumber);
      setPendingPayments(prev => {
        const newMap = new Map(prev);
        newMap.set(tableNumStr, {
          orderId: data.orderId,
          amount: data.amount,
          timestamp: Date.now()
        });
        return newMap;
      });

      // Clear any existing voice announcement interval
      if (voiceAnnouncementIntervalRef.current) {
        clearInterval(voiceAnnouncementIntervalRef.current);
        voiceAnnouncementIntervalRef.current = null;
        window.speechSynthesis.cancel();
      }

      // Function to speak the announcement using Custom Voice Service
      const speakAnnouncement = async () => {
        if (voiceEnabledRef.current) {
          const announcement = `Please confirm payment for table ${data.tableNumber}. Amount rupees ${data.amount}.`;
          
          try {
            // ✅ Use Custom Voice Service (handles Balakrishna voice if configured)
            await customVoiceService.speak(announcement, {
              voiceName: selectedVoiceRef.current // Use user-selected voice if available
            });
          } catch (error) {
            // Don't log error if it's the common 'not-allowed' error
            if (error.error !== 'not-allowed') {
              console.error('❌ Payment announcement error:', error);
            }
          }
        }
      };

      // Speak immediately
      speakAnnouncement();

      // Set up interval to repeat every 5 seconds until confirmed
      voiceAnnouncementIntervalRef.current = setInterval(speakAnnouncement, 5000);

      // Play notification sound
      addNotification({
        type: 'payment_received',
        title: '💰 Payment Request',
        message: `Table ${data.tableNumber} - ₹${data.amount} - Pay at Counter (needs confirmation)`,
        priority: 'high'
      });

    },
    onPaymentAtCounter: async (data) => {
      // Add cash animation to the table
      if (data.tableNumber) {
        const tableNumStr = String(data.tableNumber); // Normalize to string
        
        // ✅ CHECK PAYMENT MODEL FIRST - Before adding to pendingPayments
        const isEatFirst = data.paymentModel === 'eat-first' || data.paymentModel === 'eatFirst' || data.paymentModel === 'eat_first';
        
        // ✅ For EAT-FIRST: Don't add to pendingPayments, no modal needed
        if (isEatFirst) {
          // Play notification sound
          addNotification({
            type: 'payment_received',
            title: '💰 Payment Received',
            message: `Table ${data.tableNumber} - ₹${data.amount} paid (auto-confirmed)`,
            priority: 'high'
          });
          
          // Refresh dashboard to show updated table color
          setTimeout(() => loadDashboard(false), 1000);
          return; // ✅ EXIT - Don't add to Map, don't show modal
        }
        
        // ✅ For PAY-FIRST: Add to pendingPayments Map and show modal
        // Store the full order details in pendingPayments Map
        const orderDetails = {
          orderId: data.orderId,
          tableNumber: tableNumStr,
          amount: data.amount || 0,
          items: data.items || [],
          paymentModel: data.paymentModel
        };
        
        // Update pendingPayments Map
        setPendingPayments(prev => {
          const newMap = new Map(prev);
          newMap.set(tableNumStr, orderDetails);
          return newMap;
        });

        // Voice announcement for cash payment request (only for pay-first)
        if (voiceEnabledRef.current) {
          const announcement = `Please accept the payment request for table ${data.tableNumber}. Amount rupees ${data.amount}.`;
          
          try {
            // ✅ Use Custom Voice Service (handles Balakrishna voice if configured)
            await customVoiceService.speak(announcement, {
              voiceName: selectedVoiceRef.current // Use user-selected voice if available
            });
          } catch (error) {
            console.error('❌ Cash payment announcement error:', error);
          }
        }

        // Play notification sound
        addNotification({
          type: 'payment_received',
          title: '💰 Payment Received',
          message: `Table ${data.tableNumber} - ₹${data.amount} paid at counter (needs confirmation)`,
          priority: 'high'
        });
        
        if (data.orderId) {
          // Use orderDetails directly (state hasn't updated yet)
          
          // Set states to show the modal
          setPendingPaymentOrder(orderDetails);
          setShowPaymentConfirmModal(true);
        } else {
          console.error('❌ WebSocket data missing orderId!');
          console.error('   Full data:', data);
        }
      } else {
        console.error('❌ No table number in data!');
      }
    },
    onTakeawayOrderUpdate: (data) => {
      // Reload takeaway orders if modal is open
      if (showTakeAwayOrdersModal) {
        loadTakeAwayOrders();
      }
    },
    onOwnerOrderNotification: (data) => {
      // Announce via voice (when waiter/chef are OFF)
      if (data.tableNumber && data.items) {
        speakOrderAnnouncement(data.tableNumber, data.items);
      }
      
      // Show different notification based on whether approval is required
      const notificationTitle = data.requiresApproval 
        ? '💳 Payment Received - Needs Approval' 
        : '📬 New Order (Dashboards OFF)';
      
      const notificationMessage = data.requiresApproval
        ? `Table ${data.tableNumber} - ₹${data.total} paid at counter - Click to approve`
        : `Table ${data.tableNumber} - ₹${data.total} - You will handle this order`;
      
      // Show a notification badge on bell icon or auto-open notifications
      addNotification({
        type: 'owner_order',
        title: notificationTitle,
        message: notificationMessage,
        priority: 'high',
        requiresApproval: data.requiresApproval || false
      });
    }
  });

  // Manual dashboard load function (fallback if WebSocket fails)
  const loadDashboard = useCallback(async (showLoadingSpinner = true) => {
    if (!businessId) return;

    try {
      // Only show loading spinner on initial load, not during auto-refresh
      if (showLoadingSpinner) {
        setLoading(true);
      }

      const response = await qrBillingService.getDashboard(selectedFloor === 'all' ? null : selectedFloor);

      if (response.success) {
        // 🔥 DEDUPLICATE tables by tableNumber - keep the latest entry
        const rawTables = response.data.tables || [];
        const tableMap = new Map();
        rawTables.forEach(t => {
          const tableNum = t.tableNumber || t.table;
          // If duplicate exists, keep the one with more recent data
          if (!tableMap.has(tableNum) ||
            (t.amount > tableMap.get(tableNum).amount) ||
            (t.timeMs > tableMap.get(tableNum).timeMs)) {
            tableMap.set(tableNum, t);
          }
        });

        // 🔥 Force new array reference for React re-render
        const freshTables = Array.from(tableMap.values()).map(t => ({
          ...t,
          _updateTimestamp: Date.now()
        }));

        // 🚨 CRITICAL FIX: Populate pendingPayments Map for tables with pending_owner_approval orders
        const tablesWithPendingApproval = freshTables.filter(t => t.hasPendingApproval || t.has_pending_approval);
        
        if (tablesWithPendingApproval.length > 0) {
          // Fetch order details for each table and populate pendingPayments
          const fetchPendingOrders = async () => {
            const pendingOrdersMap = new Map();
            
            for (const table of tablesWithPendingApproval) {
              try {
                const tableNum = String(table.tableNumber);
                
                const API_BASE = getApiBase();
                const token = localStorage.getItem('token');
                
                const response = await fetch(`${API_BASE}/waiter/table/${tableNum}?businessId=${businessId}`, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                });
                
                if (response.ok) {
                  const data = await response.json();
                  const tableData = data.table || data;
                  
                  // Find the pending_owner_approval order
                  const pendingOrder = tableData.orders?.find(o => o.status === 'pending_owner_approval');
                  
                  if (pendingOrder) {
                    const orderDetails = {
                      orderId: pendingOrder.id,
                      tableNumber: tableNum,
                      amount: Number(pendingOrder.totalAmount || table.amount || 0),
                      items: pendingOrder.items || []
                    };
                    
                    pendingOrdersMap.set(tableNum, orderDetails);
                  }
                }
              } catch (error) {
                console.error(`❌ Failed to fetch order for table ${table.tableNumber}:`, error);
              }
            }
            
            // ✅ CRITICAL: Update pendingPayments AFTER all fetches complete
            // Merge with existing pendingPayments and clean up stale entries
            setPendingPayments(prev => {
              const newMap = new Map(prev); // Start with existing entries
              
              // Add/update new pending orders
              pendingOrdersMap.forEach((value, key) => {
                newMap.set(key, value);
              });
              
              // Remove tables that no longer have pending approval
              const pendingApprovalTableNums = new Set(tablesWithPendingApproval.map(t => String(t.tableNumber)));
              prev.forEach((value, key) => {
                if (!pendingApprovalTableNums.has(String(key))) {
                  newMap.delete(String(key));
                }
              });
              return newMap;
            });
          };
          
          fetchPendingOrders();
        } else {
          // No tables with pending approval - clean up any stale entries
          setPendingPayments(prev => {
            if (prev.size > 0) {
              return new Map();
            }
            return prev;
          });
        }

        setTables(freshTables);
        
        // ✅ Clear timestamps when sessionId changes (new customer detected)
        setConfirmedPaymentTimestamps(prev => {
          const updated = { ...prev };
          let hasChanges = false;
          
          freshTables.forEach(table => {
            const tableNum = String(table.tableNumber);
            const confirmedData = updated[tableNum];
            
            // If we have a timestamp but sessionId changed, clear it (new customer)
            if (confirmedData && table.sessionId && table.sessionId !== confirmedData.sessionId) {
              delete updated[tableNum];
              hasChanges = true;
            }
          });
          
          return hasChanges ? updated : prev;
        });
        
        setTotalRevenue(response.data.totalRevenue || 0);
        setError(null);
      }
    } catch (err) {
      console.error('❌ Dashboard load failed:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      if (showLoadingSpinner) {
        setLoading(false);
      }
      setLastUpdated(new Date());
    }
  }, [businessId, selectedFloor]);

  // Load dashboard data on mount ONLY
  useEffect(() => {
    if (!businessId) return;

    // Load available floors
    loadAvailableFloors();

    // Initial load with loading spinner
    loadDashboard(true);
  }, [businessId, loadDashboard, loadAvailableFloors]); // Fixed: Added missing dependencies

  // Log connection status changes
  useEffect(() => {
  }, [isConnected, connectionError, businessId, userId]);

  const extractTableNumber = (table) => {
    const direct = table?.tableNumber ?? table?.table;
    if (direct !== undefined && direct !== null && direct !== '') {
      const num = Number(direct);
      return Number.isFinite(num) ? num : null;
    }

    const name = String(table?.name ?? '');
    const parsed = parseInt(name.replace(/[^0-9]/g, ''), 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  // Safety-net refresh behind the WebSocket, which is what actually drives this
  // screen (onDashboardUpdate / onNewOrder / onPaymentCompleted).
  //
  // This used to be TWO overlapping intervals — one at 3s, one at 2s — both
  // calling loadDashboard, i.e. ~50 runs a minute of a heavy multi-CTE query per
  // open tab, around the clock. That is what kept the Neon compute from ever
  // suspending. Now it is a single poll that pauses when the tab is hidden and
  // backs right off while the socket is healthy; if the socket drops we fall
  // back to a brisk cadence so table colours still track.
  //
  // Pausing on a hidden tab was not enough on its own: the counter tablet is
  // left switched on with this screen in the foreground, so it never counts as
  // hidden. The poll now also stops once nobody has touched the screen for a
  // while, and fires again on the first touch.
  const dashboardPollMs = isConnected ? 30000 : 5000;
  useBackupPolling(
    useCallback(() => loadDashboard(false), [loadDashboard]),
    dashboardPollMs,
    { enabled: Boolean(businessId) }
  );

  // (Refresh-on-return is handled by useBackupPolling above, which fires the
  // callback immediately when the screen is looked at or used again.)

  // Handle closing table details modal - reset ALL timer-related state
  const handleCloseTableModal = useCallback(() => {
    setSelectedTable(null);
    setTableDetails(null);
    setTableDetailsFetchedAt(null); // Critical: Clear fetch timestamp
    setLoadingTableDetails(false);
  }, []);

  // Handle table click
  const handleTableClick = useCallback(async (table) => {
    const extractedTableNumber = extractTableNumber(table);
    if (!extractedTableNumber) {
      console.warn('⚠️ Could not determine table number. Opening modal in safe empty state.');
      setSelectedTable(table);
      setTableDetails({
        tableNumber: null,
        orders: [],
        timeMs: 0
      });
      setLoadingTableDetails(false);
      return;
    }

    // Check if this table has pending payment confirmation
    // Normalize to string for consistent comparison
    const tableNum = String(extractedTableNumber);

    // Check if table has pending payment in pendingPayments Map OR hasPendingApproval flag
    const hasPendingInMap = pendingPayments.has(tableNum);
    const hasPendingApprovalFlag = table.hasPendingApproval || table.has_pending_approval;
    const hasPendingPayment = hasPendingInMap || hasPendingApprovalFlag;

    if (hasPendingPayment) {
      // If order details already in Map, use them
      if (hasPendingInMap) {
        const orderDetails = pendingPayments.get(tableNum);
        
        setPendingPaymentOrder(orderDetails);
        setShowPaymentConfirmModal(true);
        return; // Don't show regular table modal
      }
      
      // Otherwise, fetch order details on-the-fly
      try {
        const API_BASE = getApiBase();
        const token = localStorage.getItem('token');
        
        const response = await fetch(`${API_BASE}/waiter/table/${tableNum}?businessId=${businessId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const tableData = data.table || data;
          
          // Find the pending_owner_approval order
          const pendingOrder = tableData.orders?.find(o => o.status === 'pending_owner_approval');
          
          if (pendingOrder) {
            const orderDetails = {
              orderId: pendingOrder.id,
              tableNumber: tableNum,
              amount: Number(pendingOrder.totalAmount || table.amount || 0),
              items: pendingOrder.items || []
            };
            
            // Store in Map for next time
            setPendingPayments(prev => {
              const newMap = new Map(prev);
              newMap.set(tableNum, orderDetails);
              return newMap;
            });
            
            // Show modal
            setPendingPaymentOrder(orderDetails);
            setShowPaymentConfirmModal(true);
            return; // Don't show regular table modal
          } else {
            console.error('❌ No pending_owner_approval order found for this table');
          }
        } else {
          console.error('❌ Failed to fetch order details:', response.status);
        }
      } catch (error) {
        console.error('❌ Error fetching order details:', error);
      }
      
      // If we reach here, fetch failed - show error
      showStatusModal('Failed to load order details. Please refresh and try again.', 'error', null);
      return;
    }

    // ✅ Allow opening modal even for inactive/available (ash) tables.
    // Show empty state immediately; user can still view order history.
    if (table.semantic === 'ash' || (!table.amount && !table.time)) {
      setSelectedTable(table);
      setTableDetails({
        tableNumber: extractedTableNumber,
        orders: [],
        timeMs: 0, // Always 0 for empty tables - CRITICAL FIX
        sessionStatus: 'completed' // Mark as completed so timer stays at 0
      });
      setTableDetailsFetchedAt(null); // Clear fetch time to prevent timer calculation
      setLoadingTableDetails(false);
      return;
    }

    setSelectedTable(table);
    setLoadingTableDetails(true);

    try {
      // Get table number - could be table.tableNumber or extract from table.name
      const tableNumber = extractedTableNumber;

      // Fetch real-time table details
      const token = localStorage.getItem('token');
      const API_BASE = getApiBase();
      const response = await fetch(`${API_BASE}/waiter/table/${tableNumber}?businessId=${businessId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();

        // Backend returns {success: true, table: {...}} so we need to use data.table
        const tableData = data.table || data;

        // Store when we fetched the details for live timer calculation
        const fetchTime = Date.now();
        setTableDetailsFetchedAt(fetchTime);

        // Ensure timeMs is included (from API or from dashboard table data)
        const enrichedTableData = {
          ...tableData,
          timeMs: tableData.timeMs || table.timeMs || 0
        };

        setTableDetails(enrichedTableData);

        // If no orders from API but table is active (yellow), ensure we keep timeMs
        if (!tableData.orders || tableData.orders.length === 0) {
          // Even with no orders, preserve the timer from dashboard
          setTableDetails({
            ...enrichedTableData,
            orders: table.amount > 0 ? [{
              totalAmount: table.amount,
              items: [],
              paymentStatus: table.semantic === 'green' ? 'paid' : 'pending',
              paymentMethod: 'cash'
            }] : [],
            timeMs: enrichedTableData.timeMs || table.timeMs || 0  // Always preserve timeMs
          });
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch table details:', response.status, errorText);

        // Fallback: Use data from dashboard table object
        if (table.amount > 0) {
          setTableDetails({
            tableNumber: table.tableNumber || parseInt(table.name.replace(/[^0-9]/g, '')),
            orders: [{
              totalAmount: table.amount,
              items: [],
              paymentStatus: table.semantic === 'green' ? 'paid' : 'pending',
              paymentMethod: 'cash'
            }],
            timeMs: table.timeMs || 0  // Use timeMs directly instead of startedAt to avoid UTC issues
          });
        } else {
          setTableDetails(null);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching table details:', error);

      // Fallback: Use data from dashboard table object
      if (table.amount > 0) {
        setTableDetails({
          tableNumber: table.tableNumber || parseInt(table.name.replace(/[^0-9]/g, '')),
          orders: [{
            totalAmount: table.amount,
            items: [],
            paymentStatus: table.semantic === 'green' ? 'paid' : 'pending',
            paymentMethod: 'cash'
          }],
          timeMs: table.timeMs || 0  // Use timeMs directly instead of startedAt to avoid UTC issues
        });
      } else {
        setTableDetails(null);
      }
    } finally {
      setLoadingTableDetails(false);
    }
  }, [businessId, pendingPayments]);

  // Fetch all orders for a table
  const fetchAllOrders = useCallback(async (table) => {
    try {
      const tableNumber = extractTableNumber(table);
      if (!tableNumber) {
        showStatusModal('Unable to determine table number for this table.', 'error', null);
        return;
      }

      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL;

      const response = await fetch(`${API_BASE}/waiter/table/${tableNumber}/orders?businessId=${businessId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();

        if (!data.orders || data.orders.length === 0) {
          console.warn('⚠️ API returned empty orders array');
          console.warn('⚠️ Table:', tableNumber, 'Business:', businessId);
        }

        setAllOrders(data.orders || []);
        setOrderHistoryTable(table);
        setShowOrderHistoryPage(true);
        handleCloseTableModal(); // Close table details modal and reset timer state
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch orders:', response.status, errorText);
        // Show modal anyway with empty state
        setAllOrders([]);
        setShowAllOrdersModal(true);
      }
    } catch (error) {
      console.error('❌ Error fetching orders:', error);
      // Show modal anyway with empty state
      setAllOrders([]);
      setShowAllOrdersModal(true);
    }
  }, [businessId]);

  // Helper functions for table details
  const getPaymentMethodDisplay = (method) => {
    const methodMap = {
      'online': 'UPI/Online',
      'card': 'Card',
      'cash': 'Cash',
      'test_card': 'Card'
    };
    return methodMap[method] || method || 'Cash';
  };

  const getTimeSpentMs = () => {
    // CRITICAL FIX: If selected table is empty (ash/available), always return 0
    if (selectedTable && (selectedTable.semantic === 'ash' || (!selectedTable.amount && !selectedTable.time))) {
      return 0;
    }

    // If session is completed (ash table), return 0 to show 00:00:00
    if (tableDetails?.orders?.[0]?.sessionStatus === 'completed' || tableDetails?.sessionStatus === 'completed') {
      return 0;
    }

    // If table is paid, calculate time from start to payment time (frozen timer)
    if (tableDetails?.paymentStatus === 'paid' && tableDetails?.paidAt) {
      const startTime = tableDetails.startedAt ? new Date(tableDetails.startedAt).getTime() : Date.now();
      const endTime = new Date(tableDetails.paidAt).getTime();
      const result = Math.max(0, endTime - startTime);
      return result;
    }

    // For live timer: Use timeMs from backend + elapsed time since fetch
    if (tableDetails?.timeMs !== undefined && tableDetails.timeMs !== null && tableDetailsFetchedAt) {
      const elapsedSinceFetch = nowMs - tableDetailsFetchedAt;
      const result = Math.max(0, tableDetails.timeMs + elapsedSinceFetch);
      return result;
    }

    // Fallback: Use timeMs directly if available
    if (tableDetails?.timeMs !== undefined && tableDetails.timeMs !== null) {
      const result = Math.max(0, tableDetails.timeMs);
      return result;
    }
    return 0;
  };

  const splitHMS = (ms) => {
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return [h, m, s];
  };

  const getTimeSpent = () => {
    const [hours, minutes, seconds] = splitHMS(getTimeSpentMs());
    return { hours, minutes, seconds };
  };

  const hasCurrentOrder = () => {
    // Don't check for orders while still loading
    if (loadingTableDetails) return false;

    // If session is completed (ash table), show no current orders
    if (tableDetails?.orders?.[0]?.sessionStatus === 'completed' || tableDetails?.sessionStatus === 'completed') {
      return false;
    }

    return tableDetails?.orders && tableDetails.orders.length > 0;
  };

  // Notification close handler
  const closeNotification = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  // Waiter management functions
  const buildEvenAssignments = (count, tableCount, existingAssignments = []) => {
    const safeCount = parseInt(count, 10) || 0;
    const safeTables = parseInt(tableCount, 10) || 0;
    if (!safeCount || !safeTables) return [];

    const tablesPerWaiter = Math.floor(safeTables / safeCount);
    const remainder = safeTables % safeCount;

    const newAssignments = [];
    let currentTable = 1;

    for (let i = 0; i < safeCount; i++) {
      const existing = existingAssignments[i];
      const extraTable = i < remainder ? 1 : 0;
      const tablesToAssign = tablesPerWaiter + extraTable;
      const toTable = tablesToAssign > 0 ? (currentTable + tablesToAssign - 1) : currentTable - 1;

      newAssignments.push({
        waiterName: existing?.waiterName || `Waiter ${i + 1}`,
        fromTable: tablesToAssign > 0 ? currentTable : 0,
        toTable: tablesToAssign > 0 ? toTable : 0
      });

      currentTable += tablesToAssign;
    }

    return newAssignments;
  };

  const handleWaiterCountChange = (count) => {
    const newCount = parseInt(count, 10) || 0;
    const tableCount = Number(totalTables || editableTotalTables || 0);
    const adjustedCount = tableCount > 0 ? Math.min(newCount, tableCount) : newCount;
    setWaiterCount(adjustedCount);

    if (adjustedCount === 0) {
      setWaiterAssignments([]);
      return;
    }

    const newAssignments = buildEvenAssignments(adjustedCount, tableCount, waiterAssignments);
    setWaiterAssignments(newAssignments);
  };

  // Check if all tables are assigned
  const areAllTablesAssigned = () => {
    const tableCount = Number(totalTables || editableTotalTables || 0);
    if (!tableCount || tableCount <= 0) return false;
    if (waiterAssignments.length === 0) return false;

    // Create a set of all assigned table numbers
    const assignedTables = new Set();
    
    for (const waiter of waiterAssignments) {
      const fromTable = parseInt(waiter.fromTable);
      const toTable = parseInt(waiter.toTable);
      
      // Check for valid range
      if (isNaN(fromTable) || isNaN(toTable) || fromTable <= 0 || toTable <= 0) {
        return false;
      }
      
      if (fromTable > toTable) {
        return false;
      }
      
      // Add all tables in range to set
      for (let t = fromTable; t <= toTable; t++) {
        if (assignedTables.has(t)) {
          // Duplicate assignment
          return false;
        }
        assignedTables.add(t);
      }
    }
    
    // Check if all tables from 1 to tableCount are assigned
    for (let t = 1; t <= tableCount; t++) {
      if (!assignedTables.has(t)) {
        return false;
      }
    }
    
    return true;
  };

  const updateWaiterAssignment = (index, field, value) => {
    const updated = [...waiterAssignments];
    const tableCount = Number(totalTables || editableTotalTables || 0);

    // Validate table numbers don't exceed total tables
    if (field === 'fromTable' || field === 'toTable') {
      // Allow empty string
      if (value === '' || value === null || value === undefined) {
        updated[index] = { ...updated[index], [field]: '' };
      } else {
        const numValue = parseInt(value);
        if (isNaN(numValue)) {
          updated[index] = { ...updated[index], [field]: '' };
        } else if (tableCount && numValue > tableCount) {
          updated[index] = { ...updated[index], [field]: tableCount };
        } else {
          updated[index] = { ...updated[index], [field]: numValue };
        }
      }
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }

    setWaiterAssignments(updated);
  };

  const saveWaiterAssignments = async () => {
    const tableCount = Number(totalTables || editableTotalTables || 0);
    // Validate all assignments before saving
    const hasError = waiterAssignments.some(waiter => {
      return waiter.fromTable > waiter.toTable ||
        waiter.fromTable < 0 ||
        (tableCount && waiter.toTable > tableCount);
    });

    if (hasError) {
      showStatusModal('Please ensure all table ranges are valid (From ≤ To, and within 0-' + tableCount + ')', 'warning', 1000);
      return;
    }

    // Check if all tables are assigned
    if (!areAllTablesAssigned()) {
      showStatusModal('All tables must be assigned to waiters before saving. Please ensure tables 1-' + tableCount + ' are all assigned without gaps or overlaps.', 'warning', 1000);
      return;
    }

    try {
      // Save to backend database
      const API_BASE = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_BASE}/waiter-assignments/${businessId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Business-ID': businessId
        },
        body: JSON.stringify({
          totalTables: tableCount,
          assignments: waiterAssignments.map(w => ({
            waiterName: w.waiterName,
            fromTable: w.fromTable,
            toTable: w.toTable
          }))
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to save: ${response.statusText}`);
      }

      const result = await response.json();

      // Sync waiter users with assignments
      try {
        const syncResponse = await fetch(`${API_BASE}/waiter-assignments/${businessId}/sync-users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Business-ID': businessId
          }
        });
        
        if (syncResponse.ok) {
          const syncResult = await syncResponse.json();
        } else {
          console.warn('⚠️ Failed to sync users with assignments');
        }
      } catch (syncError) {
        console.error('❌ Error syncing users:', syncError);
        // Don't fail the entire save if sync fails
      }

      // Also save to localStorage as backup
      localStorage.setItem(`waiterAssignments_${businessId}`, JSON.stringify(waiterAssignments));
      
      // Update totalTables state to match the saved value
      if (tableCount) {
        setTotalTables(tableCount);
        localStorage.setItem(`totalTables_${businessId}`, tableCount.toString());
      }

      // Show success popup instead of alert
      setShowSuccessPopup(true);
      setShowWaiterModal(false);

      // Reload dashboard to reflect new table count
      loadDashboard(true);

      // Auto-hide success popup after 3 seconds
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (error) {
      console.error('❌ Error saving waiter assignments:', error);
      showStatusModal('Failed to Save: ' + error.message, 'error', null);
    }
  };

  // Power Menu: Load current menu mode
  useEffect(() => {
    const loadMenuMode = async () => {
      if (!businessId) return;

      try {
        const API_BASE = process.env.REACT_APP_API_URL;
        const response = await fetch(`${API_BASE}/menu-mode/${businessId}`);

        if (response.ok) {
          const result = await response.json();
          setMenuMode(result.mode);
        }
      } catch (error) {
        console.warn('⚠️ Failed to load menu mode:', error);
      }
    };

    loadMenuMode();
  }, [businessId]);

  // Power Menu: Save menu mode
  const saveMenuMode = async () => {
    try {
      setLoadingMenuMode(true);

      const API_BASE = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_BASE}/menu-mode/${businessId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Business-ID': businessId
        },
        body: JSON.stringify({ mode: menuMode })
      });

      if (!response.ok) {
        throw new Error(`Failed to save: ${response.statusText}`);
      }

      const result = await response.json();

      // Close modal and show success
      setShowPowerMenuModal(false);
      setShowSuccessPopup(true);

      // Auto-hide success popup after 3 seconds
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);

    } catch (error) {
      console.error('❌ Error saving menu mode:', error);
      showStatusModal('Failed to save menu mode: ' + error.message, 'error', null);
    } finally {
      setLoadingMenuMode(false);
    }
  };

  // Power Menu: Place owner order - Navigate within same window
  const handleDiningClick = () => {
    const storedBusinessId = localStorage.getItem('businessId');

    if (!storedBusinessId) {
      showStatusModal('Business ID not found. Please log in again.', 'error', null);
      return;
    }

    // Get the menu app URL from environment variable
    const MENU_APP_URL = process.env.REACT_APP_MENU_APP_URL || 'https://order.invexis.co.in';
  const returnTo = encodeURIComponent(`${window.location.origin}${window.location.pathname}`);
    // Use hash-based routing: table=OWNER, orderType=dining, skipIntro=true to skip Get Started page
  const orderUrl = `${MENU_APP_URL}/#b=${storedBusinessId}&t=OWNER&orderType=dining&skipIntro=true&returnTo=${returnTo}`;

    // Navigate in same window instead of opening new window
    sessionStorage.setItem('invexis:return_screen_after_external', 'qr-owner-dashboard');
    sessionStorage.setItem('invexis:open_power_menu', 'true');
    window.location.href = orderUrl;
  };

  const handleTakeAwayClick = () => {
    const storedBusinessId = localStorage.getItem('businessId');

    if (!storedBusinessId) {
      showStatusModal('Business ID not found. Please log in again.', 'error', null);
      return;
    }

    // Get the menu app URL from environment variable
    const MENU_APP_URL = process.env.REACT_APP_MENU_APP_URL || 'https://order.invexis.co.in';
  const returnTo = encodeURIComponent(`${window.location.origin}${window.location.pathname}`);
    // Use hash-based routing: table=OWNER, orderType=takeaway, skipIntro=true to skip Get Started page
  const orderUrl = `${MENU_APP_URL}/#b=${storedBusinessId}&t=OWNER&orderType=takeaway&skipIntro=true&returnTo=${returnTo}`;

    // Navigate in same window instead of opening new window
    sessionStorage.setItem('invexis:return_screen_after_external', 'qr-owner-dashboard');
    sessionStorage.setItem('invexis:open_power_menu', 'true');
    window.location.href = orderUrl;
  };

  // Load Take Away Orders
  const loadTakeAwayOrders = async () => {
    if (!businessId) return;

    try {
      setLoadingTakeAwayOrders(true);
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL;


      const response = await fetch(`${API_BASE}/waiter/takeaway-orders?businessId=${businessId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Business-ID': businessId.toString()
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load takeaway orders: ${response.status}`);
      }

      const data = await response.json();
      setTakeAwayOrders(data.orders || []);

    } catch (error) {
      console.error('❌ Error loading takeaway orders:', error);
      showStatusModal(`Failed to load take away orders: ${error.message}`, 'error', null);
    } finally {
      setLoadingTakeAwayOrders(false);
    }
  };

  // Load Google Maps link
  const loadGoogleMapsLink = async () => {
    if (!businessId) return;

    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL;

      const response = await fetch(`${API_BASE}/qr-billing/google-maps-link?businessId=${businessId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Business-ID': businessId.toString()
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGoogleMapsLink(data.googleMapsLink || '');
        setGoogleMapsLinkInput(data.googleMapsLink || '');
      }
    } catch (error) {
      console.error('❌ Error loading Google Maps link:', error);
    }
  };

  // Save Google Maps link
  const saveGoogleMapsLink = async () => {
    if (!businessId) return;

    setLoadingGoogleMapsLink(true);
    setGoogleMapsLinkError('');

    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL;

      const response = await fetch(`${API_BASE}/qr-billing/google-maps-link?businessId=${businessId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Business-ID': businessId.toString(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ googleMapsLink: googleMapsLinkInput })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save Google Maps link');
      }

      setGoogleMapsLink(data.googleMapsLink || '');
      showStatusModal('✅ Google Maps link saved successfully!', 'success', 1000);
      setShowGoogleMapsModal(false);

    } catch (error) {
      console.error('❌ Error saving Google Maps link:', error);
      setGoogleMapsLinkError(error.message);
    } finally {
      setLoadingGoogleMapsLink(false);
    }
  };

  // Validate Google Maps link
  const isValidGoogleMapsLink = (link) => {
    if (!link) return true; // Allow empty

    const validPatterns = [
      /^https:\/\/maps\.app\.goo\.gl\/.+$/,
      /^https:\/\/www\.google\.com\/maps\/.+$/,
      /^https:\/\/goo\.gl\/maps\/.+$/,
      /^https:\/\/g\.page\/.+$/
    ];

    return validPatterns.some(pattern => pattern.test(link));
  };

  // Generate UPI link from UPI ID and Merchant Name
  const generateUpiLink = (upiId, merchantName) => {
    if (!upiId) return '';
    const encodedName = encodeURIComponent(merchantName || 'Merchant');
    return `upi://pay?pa=${upiId}&pn=${encodedName}`;
  };

  // Parse UPI link to extract UPI ID and Merchant Name
  const parseUpiLink = (link) => {
    if (!link) return { upiId: '', merchantName: '' };
    
    try {
      // Check if it's a standard UPI deep link
      if (link.startsWith('upi://pay?')) {
        const url = new URL(link);
        const pa = url.searchParams.get('pa') || '';
        const pn = url.searchParams.get('pn') || '';
        return { upiId: pa, merchantName: decodeURIComponent(pn) };
      }
    } catch (error) {
      console.error('Error parsing UPI link:', error);
    }
    return { upiId: '', merchantName: '' };
  };

  // Load Merchant UPI link
  const loadMerchantUpiLink = async () => {
    if (!businessId) return;

    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL;

      const response = await fetch(`${API_BASE}/qr-billing/merchant-upi-link?businessId=${businessId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Business-ID': businessId.toString()
        }
      });

      if (response.ok) {
        const data = await response.json();
        const link = data.merchantUpiLink || '';
        setMerchantUpiLink(link);
        setMerchantUpiLinkInput(link);
        
        // Parse the link to populate UPI ID and Merchant Name
        const parsed = parseUpiLink(link);
        if (parsed.upiId) {
          setUpiId(parsed.upiId);
          setMerchantName(parsed.merchantName);
          setUseManualLink(false);
        } else if (link) {
          // If link exists but can't be parsed, use manual mode
          setUseManualLink(true);
        }
      }
    } catch (error) {
      console.error('❌ Error loading merchant UPI link:', error);
    }
  };

  // Save Merchant UPI link
  const saveMerchantUpiLink = async () => {
    if (!businessId) return;

    setLoadingMerchantUpiLink(true);
    setMerchantUpiLinkError('');

    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL;

      // Generate link from UPI ID and name, or use manual input
      const linkToSave = useManualLink ? merchantUpiLinkInput : generateUpiLink(upiId, merchantName);

      if (!linkToSave) {
        setMerchantUpiLinkError('Please enter UPI ID or provide a payment link');
        setLoadingMerchantUpiLink(false);
        return;
      }

      const response = await fetch(`${API_BASE}/qr-billing/merchant-upi-link?businessId=${businessId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Business-ID': businessId.toString(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ merchantUpiLink: linkToSave })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save merchant UPI link');
      }

      setMerchantUpiLink(data.merchantUpiLink || '');
      showStatusModal('Merchant UPI payment link saved successfully!', 'success', 2500);
      setShowMerchantUpiModal(false);

    } catch (error) {
      console.error('❌ Error saving merchant UPI link:', error);
      setMerchantUpiLinkError(error.message);
    } finally {
      setLoadingMerchantUpiLink(false);
    }
  };

  // Delete Merchant UPI link
  const deleteMerchantUpiLink = async () => {
    if (!businessId) return;

    if (!window.confirm('Are you sure you want to remove the merchant UPI payment link?')) {
      return;
    }

    setLoadingMerchantUpiLink(true);
    setMerchantUpiLinkError('');

    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL;

      const response = await fetch(`${API_BASE}/qr-billing/merchant-upi-link?businessId=${businessId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Business-ID': businessId.toString(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ merchantUpiLink: null })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete merchant UPI link');
      }

      setMerchantUpiLink('');
      setMerchantUpiLinkInput('');
      setUpiId('');
      setMerchantName('');
      showStatusModal('Merchant UPI payment link removed successfully!', 'success', 2500);
      setShowMerchantUpiModal(false);

    } catch (error) {
      console.error('❌ Error deleting merchant UPI link:', error);
      setMerchantUpiLinkError(error.message);
    } finally {
      setLoadingMerchantUpiLink(false);
    }
  };

  // Validate Merchant UPI link
  const isValidMerchantUpiLink = (link) => {
    if (!link) return true; // Allow empty

    const validPatterns = [
      /^upi:\/\/pay\?pa=.+/,  // Standard UPI deep link
      /^https?:\/\/.+/         // Allow HTTP/HTTPS links
    ];

    return validPatterns.some(pattern => pattern.test(link));
  };

  // ==================== QR CODE UPLOAD FUNCTIONS ====================
  
  // Load existing payment QR image from backend
  const loadPaymentQrImage = React.useCallback(async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL;
      const url = `${API_BASE}/qr-billing/payment-qr-image?businessId=${businessId}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Business-ID': businessId
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.qrImageUrl) {
          // Remove /api/ from the base URL for static file access
          const baseUrl = API_BASE.replace('/api', '');
          const fullImageUrl = `${baseUrl}${data.qrImageUrl}`;
          
          setPaymentQrImage(data.qrImageUrl);
          setPaymentQrPreview(fullImageUrl);
        } else {
          setPaymentQrImage(null);
          setPaymentQrPreview(null);
        }
      } else {
        console.warn('⚠️ Response not OK:', response.status);
      }
    } catch (error) {
      console.error('❌ Error loading payment QR image:', error);
    }
  }, [businessId]);
  
  // Load existing payment QR image when modal opens
  useEffect(() => {
    if (showQrUploadModal && businessId) {
      loadPaymentQrImage();
    }
  }, [showQrUploadModal, businessId, loadPaymentQrImage]);

  // Handle QR image file selection
  const handleQrImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setQrUploadError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setQrUploadError('Image size must be less than 5MB');
      return;
    }

    setQrUploadError('');
    setPaymentQrImage(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPaymentQrPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Upload payment QR image
  const uploadPaymentQrImage = async () => {
    if (!paymentQrImage || typeof paymentQrImage !== 'object') {
      setQrUploadError('Please select a new image to upload');
      return;
    }

    setLoadingQrUpload(true);
    setQrUploadError('');

    try {
      const API_BASE = process.env.REACT_APP_API_URL;
      const formData = new FormData();
      formData.append('qrImage', paymentQrImage);

      const response = await fetch(`${API_BASE}/qr-billing/upload-payment-qr`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Business-ID': businessId
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update state to show uploaded image (as URL string, not File object)
        // Remove /api/ from the base URL for static file access
        const baseUrl = API_BASE.replace('/api', '');
        setPaymentQrImage(data.qrImageUrl);
        setPaymentQrPreview(`${baseUrl}${data.qrImageUrl}`);
        setQrUploadError('');
        
        // Show success message
        alert('Payment QR code uploaded successfully!');
        
        // Modal stays open showing the uploaded QR with Delete button
      } else {
        throw new Error(data.error || 'Failed to upload QR code');
      }
    } catch (error) {
      console.error('❌ Error uploading payment QR:', error);
      setQrUploadError(error.message || 'Failed to upload QR code');
    } finally {
      setLoadingQrUpload(false);
    }
  };

  // Delete payment QR image
  const deletePaymentQrImage = async () => {
    if (!window.confirm('Are you sure you want to delete the payment QR code?')) {
      return;
    }

    setLoadingQrUpload(true);

    try {
      const API_BASE = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${API_BASE}/qr-billing/payment-qr-image?businessId=${businessId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Business-ID': businessId
          }
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setPaymentQrImage(null);
        setPaymentQrPreview(null);
        setQrUploadError('');
        alert('Payment QR code deleted successfully!');
      } else {
        throw new Error(data.error || 'Failed to delete QR code');
      }
    } catch (error) {
      console.error('❌ Error deleting payment QR:', error);
      setQrUploadError(error.message || 'Failed to delete QR code');
    } finally {
      setLoadingQrUpload(false);
    }
  };

  // ==================== END QR CODE UPLOAD FUNCTIONS ====================

  // Load Google Maps link and Payment QR on mount
  useEffect(() => {
    if (businessId) {
      loadGoogleMapsLink();
      loadMerchantUpiLink();
      loadPaymentQrImage();
    }
  }, [businessId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the button and dropdown
      const isClickInsideDropdown = event.target.closest('.dropdown-menu-container');
      const isClickInsideButton = event.target.closest('.hamburger-menu-btn');

      if (showDropdownMenu && !isClickInsideDropdown && !isClickInsideButton) {
        setShowDropdownMenu(false);
      }
    };

    if (showDropdownMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdownMenu]);

  // Show Notifications Page if active
  if (showNotifications) {
    return <OwnerNotifications
      onBack={() => setShowNotifications(false)}
      businessId={businessId}
      onConfirmOrder={async (orderId) => {
        try {
          const API_BASE = process.env.REACT_APP_API_URL;
          const response = await fetch(`${API_BASE}/qr-billing/owner-confirm-order`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'X-Business-ID': businessId
            },
            body: JSON.stringify({ orderId })
          });

          const data = await response.json();

          if (response.ok && data.success) {
            // Wait for database transaction to fully commit
            await new Promise(resolve => setTimeout(resolve, 1000));
            // Refresh dashboard to update table states
            await loadDashboard(false);
          } else {
            console.error('❌ Server returned error:', data);
            throw new Error(data.error || 'Failed to confirm order');
          }
        } catch (error) {
          console.error('❌ Error confirming order:', error);
          throw error; // Re-throw to let OwnerNotifications handle it
        }
      }}
      onCancelOrder={async (orderId) => {
        try {
          const API_BASE = process.env.REACT_APP_API_URL;
          const response = await fetch(`${API_BASE}/qr-billing/owner-cancel-order`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'X-Business-ID': businessId
            },
            body: JSON.stringify({ orderId })
          });

          if (response.ok) {
            // Refresh dashboard
            await loadDashboard(false);
          } else {
            throw new Error('Failed to cancel order');
          }
        } catch (error) {
          console.error('❌ Error cancelling order:', error);
          showStatusModal('Failed to cancel order', 'error', null);
        }
      }}
    />;
  }

  return (
    <div className="rt-dashboard">
      {/* Full-Page Order History View */}
      {showOrderHistoryPage && (
        <div className="order-history-page">
          <div className="oh-header">
            <button className="oh-back-button" onClick={() => setShowOrderHistoryPage(false)}>
              <FontAwesomeIcon icon={faArrowLeft} size="lg" />
            </button>
            <h1 className="oh-title">Order History</h1>
          </div>

          <div className="oh-stats">
            <div className="oh-stat-card">
              <div className="oh-stat-value">{allOrders.length}</div>
              <div className="oh-stat-label">Total Orders</div>
            </div>
            <div className="oh-stat-card">
              <div className="oh-stat-value">{allOrders.reduce((sum, order) => sum + (order.items?.length || 0), 0)}</div>
              <div className="oh-stat-label">Items Sold</div>
            </div>
            <div className="oh-stat-card">
              <div className="oh-stat-value">₹{allOrders.reduce((sum, order) => sum + (parseFloat(order.totalAmount || order.total_amount || order.total || 0)), 0).toFixed(0)}</div>
              <div className="oh-stat-label">Revenue</div>
            </div>
          </div>

          <div className="oh-orders-list">
            {allOrders.map((order, idx) => {
              const orderTime = order.placedAt || order.placed_at || order.createdAt || order.orderTime || order.order_time || order.created_at;
              const paymentStatus = order.paymentStatus || order.payment_status || 'pending';
              const paymentMethod = order.paymentMethod || order.payment_method;
              const totalAmount = order.totalAmount || order.total_amount || order.total || 0;
              // Priority: orderNumber > order_number > order_code (from different API endpoints)
              const orderNumber = order.orderNumber || order.order_number || order.order_code || order.orderId || order.order_id;

              // Determine badge text based on payment status
              let badgeText = 'UNPAID';
              let badgeClass = 'unpaid';
              if (paymentStatus === 'paid') {
                badgeText = paymentMethod ? paymentMethod.toUpperCase() : 'PAID';
                badgeClass = paymentMethod ? paymentMethod.toLowerCase() : 'paid';
              }

              return (
                <div key={idx} className="oh-order-card">
                  <div className="oh-order-header">
                    <div className="oh-order-id">Order {orderNumber}</div>
                    <div className={`oh-payment-badge ${badgeClass}`}>
                      {badgeText}
                    </div>
                  </div>
                  <div className="oh-order-table">Table {order.tableNumber || order.table_number || orderHistoryTable?.tableNumber || orderHistoryTable?.table || orderHistoryTable?.name}</div>

                  <div className="oh-order-items">
                    <div className="oh-items-label">Items ({order.items?.length || 0})</div>
                    {order.items?.map((item, i) => (
                      <div key={i} className="oh-item">
                        {item.dishName || item.dish_name || item.name} x{item.quantity} - ₹{item.price * item.quantity}
                      </div>
                    ))}
                  </div>

                  <div className="oh-order-footer">
                    <div className="oh-order-time">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      {orderTime ? new Date(orderTime).toLocaleString('en-IN', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 'N/A'}
                    </div>
                    <div className="oh-order-total">₹{totalAmount}</div>
                  </div>
                </div>
              );
            })}
            {allOrders.length === 0 && (
              <div className="oh-empty">
                <div className="oh-empty-text">No orders found</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Real-time Notifications */}
      {notifications.length > 0 && (
        <div className="rt-notifications">
          {notifications.map((notification) => (
            <RealTimeNotification
              key={notification.id}
              notification={notification}
              onClose={() => closeNotification(notification.id)}
            />
          ))}
        </div>
      )}

      {/* Header - Hidden when ratings screen is active */}
      {activeScreen !== 'ratings' && (
        <header className="rt-header">
          <div className="rt-header-top">
            <div className="rt-header-left">
              <div className="rt-title-with-status">
                <h1 className="rt-title">INVEXIS</h1>
                {/* Single green dot for Live status */}
                {isConnected && (
                  <span className="rt-status-dot"></span>
                )}
              </div>
            </div>

            {/* Navigation menu in top right */}
            <div className="rt-header-right" style={{
              display: 'flex',
              alignItems: 'center'
            }}>
              {/* Voice Toggle Button */}
              <button
                className="rt-voice-toggle-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleVoice();
                }}
                style={{
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0px',
                  border: 'none',
                  cursor: 'pointer',
                  marginRight:'-4px'
                }}
                title={voiceEnabled ? "Voice ON - Click to disable" : "Voice OFF - Click to enable"}
              >
                {voiceEnabled ? <Volume2 size={24} color="#fff" /> : <VolumeX size={24} color="#666" />}
              </button>
              
              {/* Voice Selector Button */}
              <button
                className="rt-voice-selector-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  // Reload voices when opening modal
                  const voices = window.speechSynthesis?.getVoices() || [];
                  if (voices.length > 0) {
                    setAvailableVoices(voices);
                  }
                  setShowVoiceSelector(!showVoiceSelector);
                }}
                style={{
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  marginRight: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
                title="Select Voice"
              >
                🎤
              </button>
              
              {/* Home Icon - Always visible (Navigate to Dashboard) */}
              <button
                onClick={() => {
                  // Store that we came from owner dashboard
                  localStorage.setItem('previousDashboard', 'qr-owner-dashboard');
                  goTo && goTo('dashboard');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  fontSize: '20px',
                  paddingRight: '0px',
                }}
                aria-label="Go to Dashboard"
                title="Go to Dashboard"
              >
                <Home size={22} />
              </button>
              
              {/* Bell Notification - Only show when both chef and waiter dashboards are OFF */}
              {!waiterDashboardEnabled && !chefDashboardEnabled && (
                <button
                  className="rt-notification-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowNotifications(true);
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0px',
                    border: 'none',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  title="Notifications"
                >
                  <Bell size={24} color="#fff" />
                </button>
              )}

              <button
                className="rt-menu-btn hamburger-menu-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropdownMenu(!showDropdownMenu);
                }}
                title="Menu"
              >
                <MenuIcon size={20} />
              </button>

              {/* Dropdown Menu */}
              {showDropdownMenu && (
                <div className="dropdown-menu-container rt-hamburger-menu">
                  <button
                    className="rt-hamburger-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPowerMenuModal(true);
                      setShowDropdownMenu(false);
                    }}
                  >
                    <span className="rt-h-item-label">Power Menu</span>
                  </button>

                  <button
                    className="rt-hamburger-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowWaiterModal(true);
                      setShowDropdownMenu(false);
                    }}
                  >
                    <span className="rt-h-item-label">Manage Waiters/Floors</span>
                  </button>

                  <button
                    className="rt-hamburger-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      onNavigateToMenu();
                      setShowDropdownMenu(false);
                    }}
                  >
                    <span className="rt-h-item-label">Menu Management</span>
                  </button>

                  <button
                    className="rt-hamburger-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (goTo) {
                        goTo('qr-billing-onboarding-edit');
                      } else {
                        console.error('❌ goTo function not available');
                      }
                      setShowDropdownMenu(false);
                    }}
                  >
                    <span className="rt-h-item-label">Table Configuration</span>
                  </button>

                  <button
                    className="rt-hamburger-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveScreen('ratings');
                      setShowDropdownMenu(false);
                    }}
                  >
                    <span className="rt-h-item-label">Ratings</span>
                  </button>

                  <button
                    className="rt-hamburger-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowVideoUploadModal(true);
                      setShowDropdownMenu(false);
                    }}
                  >
                    <span className="rt-h-item-label">Upload Video</span>
                  </button>

                  <button
                    className="rt-hamburger-item"
                    onClick={() => {
                      setShowGoogleMapsModal(true);
                      setShowDropdownMenu(false);
                    }}
                  >
                    <span className="rt-h-item-label">Google Maps Review Link</span>
                  </button>

                  <button
                    className="rt-hamburger-item"
                    onClick={() => {
                      setShowMerchantUpiModal(true);
                      setShowDropdownMenu(false);
                    }}
                  >
                    <span className="rt-h-item-label">Merchant UPI Link</span>
                  </button>

                  <button
                    className="rt-hamburger-item"
                    onClick={() => {
                      setShowQrUploadModal(true);
                      setShowDropdownMenu(false);
                    }}
                  >
                    <span className="rt-h-item-label">Upload Payment QR Code</span>
                  </button>

                  <div className="rt-hamburger-item rt-hamburger-item-toggle" onClick={(e) => e.stopPropagation()}>
                    <div className="rt-h-item-left">
                      <span className="rt-h-item-label">Waiter Dashboard</span>
                    </div>
                    <label
                      onClick={(e) => e.stopPropagation()}
                      className={`rt-switch ${waiterDashboardEnabled ? 'on' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={waiterDashboardEnabled}
                        onChange={async (e) => {
                          e.stopPropagation();
                          const newState = !waiterDashboardEnabled;
                          
                          // If turning OFF, show warning first
                          if (!newState) {
                            setPendingToggleChange({ type: 'waiter', newState });
                            setShowToggleWarningModal(true);
                            return;
                          }
                          
                          // If turning ON, proceed directly
                          setWaiterDashboardEnabled(newState);

                          // Save to backend
                          try {
                            const API_BASE = process.env.REACT_APP_API_URL;
                            await fetch(`${API_BASE}/qr-billing/dashboard-toggles`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                'X-Business-ID': businessId
                              },
                              body: JSON.stringify({
                                waiterEnabled: newState,
                                chefEnabled: chefDashboardEnabled
                              })
                            });
                          } catch (error) {
                            console.error('❌ Failed to save toggle state:', error);
                          }
                        }}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span className="rt-switch-track"><span className="rt-switch-thumb"></span></span>
                    </label>
                  </div>



                  <div className="rt-hamburger-item rt-hamburger-item-toggle" onClick={(e) => e.stopPropagation()}>
                    <div className="rt-h-item-left">
                      <span className="rt-h-item-label">Chef Dashboard</span>
                    </div>
                    <label
                      onClick={(e) => e.stopPropagation()}
                      className={`rt-switch ${chefDashboardEnabled ? 'on' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={chefDashboardEnabled}
                        onChange={async (e) => {
                          e.stopPropagation();
                          const newState = !chefDashboardEnabled;
                          
                          // If turning OFF, show warning first
                          if (!newState) {
                            setPendingToggleChange({ type: 'chef', newState });
                            setShowToggleWarningModal(true);
                            return;
                          }
                          
                          // If turning ON, proceed directly
                          setChefDashboardEnabled(newState);

                          // Save to backend
                          try {
                            const API_BASE = process.env.REACT_APP_API_URL;
                            await fetch(`${API_BASE}/qr-billing/dashboard-toggles`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                'X-Business-ID': businessId
                              },
                              body: JSON.stringify({
                                waiterEnabled: waiterDashboardEnabled,
                                chefEnabled: newState
                              })
                            });
                          } catch (error) {
                            console.error('❌ Failed to save toggle state:', error);
                          }
                        }}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span className="rt-switch-track"><span className="rt-switch-thumb"></span></span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Horizontal divider line */}
          <div className="rt-header-divider"></div>
        </header>
      )}

      {/* Main Content */}
      <main className="rt-main">
        {loading && tables.length === 0 ? (
          <div className="rt-loading">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
              <div style={{ position: 'relative', width: '95px', height: '95px' }}>
                <img 
                  src="/invexis-logo.png"
                  alt="Invexis"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '70px',
                    height: '70px',
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    padding: '0px',
                    pointerEvents: 'none',
                    objectFit: 'contain',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                    zIndex: 1
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '70px',
                  height: '70px',
                  border: '6px solid #000000',
                  borderTop: '6px solid #f9c806',
                  borderRight: '6px solid #f9c806',
                  borderRadius: '50%',
                  animation: 'ownerDashboardSpin 1s linear infinite',
                  zIndex: 16
                }}></div>
              </div>
              <p style={{ color: '#666', fontSize: '16px', margin: 0 }}>Loading dashboard...</p>
            </div>
            <style>{`
              @keyframes ownerDashboardSpin { 
                0% { transform: translate(-50%, -50%) rotate(0deg); } 
                100% { transform: translate(-50%, -50%) rotate(360deg); } 
              }
            `}</style>
          </div>
        ) : error ? (
          <div className="rt-error">
            <p>❌ {error}</p>
            <button onClick={loadDashboard} className="rt-retry-btn">
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Ratings Dashboard Screen */}
            {activeScreen === 'ratings' && (
              <RatingsDashboard
                businessId={businessId}
                onClose={() => setActiveScreen('dashboard')}
              />
            )}

            {/* Default Dashboard Screen */}
            {activeScreen === 'dashboard' && (
              <>
                {/* Tables Section */}
                <section className="rt-tables-section">
                  {/* Dashboard heading */}
                  <div className="rt-dashboard-row">
                    <h2
                      className="rt-dashboard-text"
                      onClick={() => setActiveScreen('dashboard')}
                      style={{
                        cursor: activeScreen !== 'dashboard' ? 'pointer' : 'default'
                      }}
                    >
                      Dashboard
                    </h2>
                  </div>

                  {/* Restaurant Floor heading with Filter */}
                  <div className="rt-section-header">
                    <h2 className="rt-section-title">
                      Restaurant Floor - {tables.filter(t => t.tableNumber !== 'TAKEAWAY' && t.tableNumber !== 'OWNER').length} Tables
                    </h2>
                    {/* Floor Filter Dropdown - custom (Usage-style) */}
                    <div className="rt-filter-dropdown" ref={floorMenuRef}>
                      <button
                        type="button"
                        className="rt-filter-trigger"
                        onClick={() => setShowFloorMenu(v => !v)}
                        aria-haspopup="listbox"
                        aria-expanded={showFloorMenu}
                        title="Filter floors"
                      >
                        <span className="rt-filter-left">
                          <Filter size={16} />
                          <span className="rt-filter-label">{selectedFloor === 'all' ? 'Filter' : selectedFloor}</span>
                        </span>
                      </button>
                      {showFloorMenu && (
                        <div className="rt-filter-menu" role="listbox">
                          <div
                            className="rt-filter-option"
                            role="option"
                            aria-selected={selectedFloor === 'all'}
                            onClick={() => { setSelectedFloor('all'); setShowFloorMenu(false); }}
                          >
                            All Floors
                          </div>
                          {availableFloors.map((floor, idx) => (
                            <div
                              key={idx}
                              className={`rt-filter-option${selectedFloor === floor ? ' selected' : ''}`}
                              role="option"
                              aria-selected={selectedFloor === floor}
                              onClick={() => { setSelectedFloor(floor); setShowFloorMenu(false); }}
                            >
                              {floor}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Main Counter Card below Restaurant Floor heading */}
                  <div className="rt-stats-section-inline">
                    <RealTimeStats
                      tables={tables}
                      totalRevenue={totalRevenue}
                      onClick={() => {
                        setShowTakeAwayOrdersModal(true);
                        loadTakeAwayOrders();
                      }}
                    />
                  </div>

                  {/* Dynamic Table Grid */}
                  <div className="rt-tables-grid" style={{ minHeight: loading && tables.length === 0 ? '400px' : 'auto' }}>
                    {loading && tables.length === 0 ? (
                      // Show skeleton loader only on initial load
                      Array.from({ length: 6 }).map((_, idx) => (
                        <div key={`skeleton-${idx}`} className="rt-table-card-skeleton">
                          <div className="skeleton-header"></div>
                          <div className="skeleton-status"></div>
                          <div className="skeleton-footer"></div>
                        </div>
                      ))
                    ) : tables.length === 0 ? (
                      <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: '#666' }}>
                        <p style={{ fontSize: '18px', margin: '10px 0' }}>No tables configured yet</p>
                        <p style={{ fontSize: '14px', margin: '10px 0' }}>Configure tables in Settings to get started</p>
                      </div>
                    ) : (
                      tables
                        .filter(table => table.tableNumber !== 'TAKEAWAY' && table.tableNumber !== 'OWNER') // Hide TAKEAWAY and OWNER tables
                        .map((table) => (
                          <DynamicTableCard
                            key={`table-${table.tableNumber}`}
                            table={table}
                            onTableClick={handleTableClick}
                            isFlashing={flashingTables.has(table.tableNumber)}
                            hasCashAnimation={
                              pendingPayments.has(String(table.tableNumber)) || 
                              table.hasPendingApproval || 
                              table.has_pending_approval
                            }
                            confirmedPaymentTimestamps={confirmedPaymentTimestamps}
                          />
                        ))
                    )}
                  </div>

                  {/* Real-time Legend */}
                  <div className="rt-legend">
                    <div className="rt-legend-title">Live Status Legend</div>
                    <div className="rt-legend-items">
                      <div className="rt-legend-item">
                        <span className="rt-legend-dot rt-dot-ash"></span>
                        <span>Available</span>
                      </div>
                      <div className="rt-legend-item">
                        <span className="rt-legend-dot rt-dot-orange"></span>
                        <span>Cash</span>
                      </div>
                      <div className="rt-legend-item">
                        <span className="rt-legend-dot rt-dot-red"></span>
                        <span>Needs Attention</span>
                      </div>
                      <div className="rt-legend-item">
                        <span className="rt-legend-dot rt-dot-yellow"></span>
                        <span>Occupied</span>
                      </div>
                      <div className="rt-legend-item">
                        <span className="rt-legend-dot rt-dot-green"></span>
                        <span>Paid</span>
                      </div>
                    </div>
                  </div>
                </section>
              </>
            )}
          </>
        )}
      </main>

      {/* Table Details Modal */}
      {selectedTable && (
        <div className="odtd-overlay" onClick={handleCloseTableModal}>
          <div className="odtd-modal" onClick={e => e.stopPropagation()}>
            <div className="odtd-header">
              <h3 className="odtd-title">{selectedTable.name} Details</h3>
              <button className="odtd-close" onClick={handleCloseTableModal}>×</button>
            </div>
            <div className="odtd-body">
              {loadingTableDetails ? (
                <div className="odtd-loading">Loading table details...</div>
              ) : hasCurrentOrder() ? (
                <>
                  {/* Current Orders */}
                  <div className="odtd-section odtd-current-orders">
                    <div className="odtd-section-title">Current Orders</div>
                    {(() => {
                      // Get the most recent order based on creation time
                      const orders = tableDetails?.orders || [];

                      // Sort orders by created time (most recent first)
                      const sortedOrders = [...orders].sort((a, b) => {
                        const timeA = new Date(a.createdAt || a.placedAt || a.created_at || 0).getTime();
                        const timeB = new Date(b.createdAt || b.placedAt || b.created_at || 0).getTime();
                        return timeB - timeA; // Descending order (most recent first)
                      });

                      // Find the latest unpaid order, or if all paid, show the most recent order
                      const latestUnpaidOrder = sortedOrders.find(o => o.paymentStatus !== 'paid');
                      const latestOrder = latestUnpaidOrder || sortedOrders[0];

                      if (!latestOrder || !latestOrder.items || latestOrder.items.length === 0) {
                        return (
                          <div className="odtd-items">
                            <div className="odtd-item" style={{ opacity: 0.5 }}>
                              <div>No current orders</div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <>
                          <div className="odtd-items">
                            {latestOrder.items.map((item, idx) => (
                              <div key={idx} className="odtd-item">
                                <div>
                                  <div className="odtd-item-name">{item.name || item.item_name} <span className="odtd-item-qty">X {item.quantity || item.qty}</span></div>
                                </div>
                                <div className="odtd-item-price">₹{Number((item.price || item.price_at_order || 0) * (item.quantity || item.qty || 1)).toFixed(2)}</div>
                              </div>
                            ))}
                          </div>

                          <div className="odtd-separator-line"></div>
                          <div className="odtd-row odtd-row-between" style={{ marginTop: 10 }}>
                            <div className="odtd-label">Order Total</div>
                            <div className="odtd-amount">₹{Number(latestOrder.totalAmount || latestOrder.total_amount || 0).toFixed(2)}</div>
                          </div>
                        </>
                      );
                    })()}

                  </div>

                  {/* Time Spent */}
                  <div className="odtd-section odtd-time-section">
                    <div className="odtd-section-title">Time Spent</div>
                    <div className="odtd-time">
                      {(() => {
                        const { hours, minutes, seconds } = getTimeSpent();
                        const pad = (n) => String(n).padStart(2, '0');
                        return (
                          <>
                            <div className="odtd-time-box"><div className="odtd-time-number">{pad(hours)}</div><div className="odtd-time-label">Hours</div></div>
                            <div className="odtd-time-box"><div className="odtd-time-number">{pad(minutes)}</div><div className="odtd-time-label">Minutes</div></div>
                            <div className="odtd-time-box"><div className="odtd-time-number">{pad(seconds)}</div><div className="odtd-time-label">Seconds</div></div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Payment Details */}
                  <div className="odtd-section odtd-payment">
                    <div className="odtd-section-title">Payment Details</div>
                    <div className="odtd-card-row">
                      <div className="odtd-card-left">
                        <div className="odtd-label">Payment Method</div>
                      </div>
                      <div className="odtd-card-right">
                        {(() => {
                          const firstOrder = tableDetails?.orders?.[0];
                          const paymentStatus = firstOrder?.paymentStatus || firstOrder?.payment_status;
                          const paymentMethod = firstOrder?.paymentMethod || firstOrder?.payment_method;

                          return paymentStatus === 'paid'
                            ? getPaymentMethodDisplay(paymentMethod)
                            : <span className="odtd-status odtd-status--pending">Pending</span>;
                        })()}
                      </div>
                    </div>
                    <div className="odtd-line" />
                  </div>
                </>
              ) : (
                <>
                  {/* Current Orders (Empty State) */}
                  <div className="odtd-section odtd-current-orders">
                    <div className="odtd-section-title">Current Orders</div>
                    <div className="odtd-items">
                      <div className="odtd-item" style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'column', textAlign: 'center', padding: '14px 10px', gap: '4px' }}>
                        <div className="odtd-empty-icon" aria-hidden>🍽</div>
                        <div className="odtd-empty-title">No Orders Yet</div>
                        <div className="odtd-empty-sub">This table has no current orders</div>
                      </div>
                    </div>
                    <div className="odtd-separator-line"></div>
                    <div className="odtd-row odtd-row-between" style={{ marginTop: 10 }}>
                      <div className="odtd-label">Order Total</div>
                      <div className="odtd-amount">₹0.00</div>
                    </div>
                  </div>

                  {/* Time Spent (show live timer even with no orders) */}
                  <div className="odtd-section odtd-time-section">
                    <div className="odtd-section-title">Time Spent</div>
                    <div className="odtd-time">
                      {(() => {
                        const { hours, minutes, seconds } = getTimeSpent();
                        const pad = (n) => String(n).padStart(2, '0');
                        return (
                          <>
                            <div className="odtd-time-box"><div className="odtd-time-number">{pad(hours)}</div><div className="odtd-time-label">Hours</div></div>
                            <div className="odtd-time-box"><div className="odtd-time-number">{pad(minutes)}</div><div className="odtd-time-label">Minutes</div></div>
                            <div className="odtd-time-box"><div className="odtd-time-number">{pad(seconds)}</div><div className="odtd-time-label">Seconds</div></div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Payment Details (Empty/Pending) */}
                  <div className="odtd-section odtd-payment">
                    <div className="odtd-section-title">Payment Details</div>
                    <div className="odtd-card-row">
                      <div className="odtd-card-left">
                        <div className="odtd-label">Payment Method</div>
                      </div>
                      <div className="odtd-card-right" style={{ opacity: 0.7 }}>—</div>
                    </div>
                    <div className="odtd-line" />
                  </div>
                </>
              )}
              <button className={hasCurrentOrder() ? "odtd-primary" : "odtd-primary odtd-primary--gold"} onClick={() => fetchAllOrders(selectedTable)}>
                View all orders
              </button>
            </div>
          </div>
        </div>
      )}

      {/* All Orders Modal */}
      {showAllOrdersModal && (
        <div className="tgd-overlay" onClick={() => setShowAllOrdersModal(false)}>
          <div className="tgd-modal" onClick={e => e.stopPropagation()}>
            <div className="tgd-header">
              <h3 className="tgd-title">All Orders - {selectedTable?.name}</h3>
              <button className="tgd-close" onClick={() => setShowAllOrdersModal(false)}>×</button>
            </div>
            <div className="tgd-body">
              {allOrders.length === 0 ? (
                <div className="odtd-empty">
                  <div className="odtd-empty-icon" aria-hidden>📋</div>
                  <div className="odtd-empty-title">No Orders Yet</div>
                  <div className="odtd-empty-sub">No orders have been placed on this table today</div>
                </div>
              ) : (
                <div className="t2-orders-list">
                  {allOrders.map((order, idx) => {
                    // Use orderNumber (8-char code) instead of full orderId
                    const displayOrderId = order.orderNumber || order.order_number || order.order_code || 'N/A';

                    // Get order time - try multiple field names
                    const orderTimeStr = order.placedAt || order.placed_at || order.createdAt || order.orderTime || order.order_time;

                    // Parse the timestamp - PostgreSQL returns UTC timestamps
                    let orderTime = null;
                    if (orderTimeStr) {
                      // Create Date object from UTC timestamp
                      orderTime = new Date(orderTimeStr);
                    }

                    // Format time in IST (India Standard Time)
                    const formatTime = (date) => {
                      if (!date || isNaN(date.getTime())) return 'N/A';

                      // Get IST time components
                      const istString = date.toLocaleString('en-US', {
                        timeZone: 'Asia/Kolkata',
                        hour12: true,
                        hour: 'numeric',
                        minute: '2-digit',
                        second: '2-digit'
                      });

                      return istString.toLowerCase();
                    };

                    return (
                      <div key={order.orderId || order.order_id || idx} className="t2-order-card">
                        <div className="t2-order-header">
                          <div className="t2-order-number">
                            Order #{displayOrderId}
                          </div>
                          <div className="t2-order-time">
                            {formatTime(orderTime)}
                          </div>
                        </div>
                        <div className="t2-order-items">
                          {(order.items || []).map((item, itemIdx) => (
                            <div key={itemIdx} className="t2-order-item">
                              <span>{item.name || item.item_name}          x{item.quantity}</span>
                              <span>₹{(item.price || item.price_at_order || 0).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="t2-order-footer">
                          <div className="t2-order-total">Total: ₹{(order.totalAmount || order.total_amount || 0).toFixed(2)}</div>
                          <div className={`t2-order-status ${order.paymentStatus || order.payment_status}`}>
                            {(order.paymentStatus || order.payment_status) === 'paid' ? 'Paid' : 'Pending'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Waiter Assignment Modal */}
      {showWaiterModal && (
        <div className="waiter-modal-overlay waiter-mgmt-modal-unique" onClick={() => setShowWaiterModal(false)}>
          <div className="waiter-modal-container waiter-mgmt-container-unique" onClick={e => e.stopPropagation()}>
            <div className="waiter-modal-header">
              <button 
                className="waiter-modal-back" 
                onClick={() => setShowWaiterModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ffffff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '20px',
                  padding: '0',
                  marginRight: '2px'
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <h3 className="waiter-modal-title">
                {waiterModalActiveTab === 'Waiters' ? 'Waiter Management' : 'Floor Management'}
              </h3>
            </div>
            <div className="waiter-modal-body">
              {/* Tabs: Waiters / Floors */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button
                  onClick={() => setWaiterModalActiveTab('Waiters')}
                  className={"waiter-tab-btn waiters-tab" + (waiterModalActiveTab === 'Waiters' ? ' active' : '')}
                  style={{ padding: '8px 12px', borderRadius: '6px' }}
                >
                  Waiters
                </button>
                <button
                  onClick={() => setWaiterModalActiveTab('Floors')}
                  className={"waiter-tab-btn floors-tab" + (waiterModalActiveTab === 'Floors' ? ' active' : '')}
                  style={{ padding: '8px 12px', borderRadius: '6px' }}
                >
                  Floors
                </button>
              </div>

              {waiterModalActiveTab === 'Waiters' && (
                <>
                  {/* Restaurant Settings Card */}
                  <div className="waiter-assignment-card" style={{ marginBottom: '20px' }}>
                    <div className="waiter-assignment-row">
                      <label style={{ color: '#f3f4f6', fontSize: '1rem', whiteSpace: 'nowrap' }}>
                        Total Tables in Restaurant:
                      </label>
                      <input
                        type="number"
                        value={totalTables || ''}
                        placeholder="0"
                        className="waiter-count-input"
                        readOnly
                        disabled
                        title="Total tables are set during onboarding"
                        style={{ width: '80px', opacity: 0.7, cursor: 'not-allowed' }}
                      />
                    </div>

                    <div className="waiter-assignment-row" style={{ marginBottom: '0' }}>
                      <label style={{ color: '#f3f4f6', fontSize: '1rem', whiteSpace: 'nowrap' }}>
                        Number of Waiters:
                      </label>
                      <input
                        type="number"
                        max="10"
                        value={waiterCount}
                        className="waiter-count-input"
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || val === null) {
                            setWaiterCount('');
                          } else {
                            handleWaiterCountChange(val);
                          }
                        }}
                        onKeyDown={(e) => {
                          // Allow backspace, delete, arrows, tab
                          if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                            return;
                          }
                          // Prevent non-numeric input
                          if (!/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <h4 className="waiter-section-title">
                      Assign Tables to Waiters
                    </h4>
                    {waiterAssignments.map((waiter, idx) => (
                      <div key={idx} className="waiter-assignment-card">
                        {/* Table Summary as Heading at Top */}
                        <div className="waiter-table-summary">
                          Tables: {waiter.fromTable} - {waiter.toTable} ({Math.max(0, waiter.toTable - waiter.fromTable + 1)} tables)
                        </div>

                        {/* Waiter Name - Label on left, input on right */}
                        <div className="waiter-assignment-row">
                          <label style={{ color: '#fff', fontSize: '14px', whiteSpace: 'nowrap' }}>
                            Waiter Name:
                          </label>
                          <input
                            type="text"
                            value={waiter.waiterName}
                            onChange={(e) => updateWaiterAssignment(idx, 'waiterName', e.target.value)}
                            placeholder={`Waiter ${idx + 1}`}
                            className="waiter-name-input"
                          />
                        </div>

                        {/* From Table and To Table in same line */}
                        <div className="waiter-table-row">
                          <div className="waiter-table-group">
                            <label style={{ color: '#fff', fontSize: '14px', whiteSpace: 'nowrap' }}>
                              From Table:
                            </label>
                            <input
                              type="number"
                              max={totalTables}
                              value={waiter.fromTable}
                              className="waiter-table-input"
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || val === null) {
                                  updateWaiterAssignment(idx, 'fromTable', '');
                                } else {
                                  updateWaiterAssignment(idx, 'fromTable', val);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                                  return;
                                }
                                if (!/[0-9]/.test(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                            />
                          </div>

                          <div className="waiter-table-group">
                            <label style={{ color: '#fff', fontSize: '14px', whiteSpace: 'nowrap' }}>
                              To Table:
                            </label>
                            <input
                              type="number"
                              max={totalTables}
                              value={waiter.toTable}
                              className="waiter-table-input"
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || val === null) {
                                  updateWaiterAssignment(idx, 'toTable', '');
                                } else {
                                  updateWaiterAssignment(idx, 'toTable', val);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                                  return;
                                }
                                if (!/[0-9]/.test(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {!areAllTablesAssigned() && (
                    <div style={{
                      backgroundColor: 'transparent',
                      color: '#fff',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      marginBottom: '12px',
                      fontSize: '13px',
                      lineHeight: '1.5'
                    }}>
                      <div style={{ marginBottom: '0' }}><span style={{ fontWeight: 'bold', color: '#FFD700', fontSize: '14px' }}>Note: </span>All tables (1-{totalTables || editableTotalTables}) must be assigned to waiters before</div>
                      <div style={{ marginTop: '0', paddingLeft: '0', marginRight: '16px' }}>saving. Please ensure no gaps or overlaps.</div>
                    </div>
                  )}

                  <div className="waiter-button-group">
                    <button
                      onClick={() => setShowWaiterModal(false)}
                      className="waiter-btn-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveWaiterAssignments}
                      className="waiter-btn-save"
                      disabled={!areAllTablesAssigned()}
                      style={{
                        opacity: areAllTablesAssigned() ? 1 : 0.5,
                        cursor: areAllTablesAssigned() ? 'pointer' : 'not-allowed'
                      }}
                      title={!areAllTablesAssigned() ? 'All tables must be assigned before saving' : ''}
                    >
                      Save
                    </button>
                  </div>
                </>
              )}

              {waiterModalActiveTab === 'Floors' && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <FloorManagement businessId={businessId} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toggle Warning Modal */}
      {showToggleWarningModal && pendingToggleChange && (
        <div 
          className="toggle-warning-overlay"
          onClick={() => {
            setShowToggleWarningModal(false);
            setPendingToggleChange(null);
          }}
        >
          <div 
            className="toggle-warning-modal"
            onClick={e => e.stopPropagation()}
          >
            <div className="toggle-warning-icon">
              ⚠️
            </div>
            <h3 className="toggle-warning-title">Warning</h3>
            <p className="toggle-warning-message">
              {pendingToggleChange.type === 'waiter' 
                ? 'Turning this off will stop all live orders on the Waiter Dashboard.'
                : 'Turning this off will stop all live orders on the Chef Dashboard.'}
            </p>
            
            <div className="toggle-warning-buttons">
              <button
                className="toggle-warning-btn toggle-warning-btn-cancel"
                onClick={() => {
                  setShowToggleWarningModal(false);
                  setPendingToggleChange(null);
                }}
              >
                Cancel
              </button>
              <button
                className="toggle-warning-btn toggle-warning-btn-confirm"
                onClick={async () => {
                  const { type, newState } = pendingToggleChange;
                  
                  // Update state
                  if (type === 'waiter') {
                    setWaiterDashboardEnabled(newState);
                  } else {
                    setChefDashboardEnabled(newState);
                  }

                  // Save to backend
                  try {
                    const API_BASE = process.env.REACT_APP_API_URL;
                    await fetch(`${API_BASE}/qr-billing/dashboard-toggles`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'X-Business-ID': businessId
                      },
                      body: JSON.stringify({
                        waiterEnabled: type === 'waiter' ? newState : waiterDashboardEnabled,
                        chefEnabled: type === 'chef' ? newState : chefDashboardEnabled
                      })
                    });
                  } catch (error) {
                    console.error('❌ Failed to save toggle state:', error);
                  }
                  
                  // Close modal
                  setShowToggleWarningModal(false);
                  setPendingToggleChange(null);
                }}
              >
                Yes, Disable
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Power Menu Modal */}
      {showPowerMenuModal && (
        <div className="power-menu-overlay power-menu-overlay-unique"
          onClick={() => setShowPowerMenuModal(false)}>
          <div className="power-menu-modal power-menu-modal-unique"
            onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button
              className="power-menu-close power-menu-close-unique"
              onClick={() => setShowPowerMenuModal(false)}
            >×</button>

            {/* Header */}
            <div className="power-menu-header power-menu-header-unique">
              <h2 className="power-menu-title power-menu-title-unique">
                Power Menu
              </h2>
              <p className="power-menu-subtitle power-menu-subtitle-unique">
                Choose which menu mode your customers will see
              </p>
            </div>

            {/* Current Mode Indicator */}
            <div className="power-menu-current power-menu-current-unique">
              <span className="power-menu-current-label power-menu-current-label-unique">Current Mode: </span>
              <span className="power-menu-current-value power-menu-current-value-unique">
                {menuMode === 'takeaway' ? ' TAKE AWAY' : ' DINING'}
              </span>
            </div>

            {/* Mode Options */}
            <div className="power-menu-options power-menu-options-unique">
              {/* Take Away Card */}
              <div
                onClick={handleTakeAwayClick}
                className={`power-menu-card power-menu-card-unique ${menuMode === 'takeaway' ? 'active' : ''}`}
              >
                <div className="power-menu-icon power-menu-icon-unique">
                  <ShoppingBag size={48} strokeWidth={1.5} />
                </div>
                <h3 className="power-menu-card-title power-menu-card-title-unique">
                  Take Away
                </h3>
              </div>

              {/* Dining Card */}
              <div
                onClick={handleDiningClick}
                className={`power-menu-card power-menu-card-unique ${menuMode === 'dining' ? 'active' : ''}`}
              >
                <div className="power-menu-icon power-menu-icon-unique">
                  <FontAwesomeIcon icon={faUtensils} style={{ fontSize: '40px' }} />
                </div>
                <h3 className="power-menu-card-title power-menu-card-title-unique">
                  Dining
                </h3>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Take Away Orders Modal - Full Page */}
      {showTakeAwayOrdersModal && (
        <div onClick={() => setShowTakeAwayOrdersModal(false)} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 0
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: '#111518',
            borderRadius: '0',
            width: '600px',
            maxWidth: '600px',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
            className="take-away-modal-no-scrollbar"
          >
            {/* Header */}
            <div style={{
              padding: '1rem 1rem 1rem 0.5rem',
              borderBottom: '1px solid #111518',
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              backgroundColor: '#111518',
              flexShrink: 0
            }}>
              <button
                onClick={() => setShowTakeAwayOrdersModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#f1f5f9',
                  cursor: 'pointer',
                  fontSize: '22px',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} size="sm" />
              </button>
              <h2 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#f1f5f9',
                flex: 1,
                textAlign: 'left'
              }}>
                Take Away Orders
              </h2>
            </div>

            {/* Scrollable Content - Stats Boxes and Orders */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
              {/* Stats Boxes */}
              {!loadingTakeAwayOrders && takeAwayOrders.length > 0 && (
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  padding: '20px 24px'
                }}>
                  <div style={{
                    flex: 1,
                    background: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '12px',
                    padding: '20px 16px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '32px',
                      fontWeight: '700',
                      color: '#ffffff',
                      marginBottom: '4px'
                    }}>
                      {takeAwayOrders.length}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#9ca3af',
                      fontWeight: '400'
                    }}>
                      Total Orders
                    </div>
                  </div>
                  <div style={{
                    flex: 1,
                    background: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '12px',
                    padding: '20px 16px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '32px',
                      fontWeight: '700',
                      color: '#ffffff',
                      marginBottom: '4px'
                    }}>
                      {takeAwayOrders.reduce((sum, order) => sum + (order.totalItems || 0), 0)}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#9ca3af',
                      fontWeight: '400'
                    }}>
                      Items Sold
                    </div>
                  </div>
                  <div style={{
                    flex: 1,
                    background: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '12px',
                    padding: '20px 16px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '32px',
                      fontWeight: '700',
                      color: '#ffffff',
                      marginBottom: '4px'
                    }}>
                      ₹{takeAwayOrders.reduce((sum, order) => sum + (order.totalAmount || 0), 0).toFixed(0)}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#9ca3af',
                      fontWeight: '400'
                    }}>
                      Revenue
                    </div>
                  </div>
                </div>
              )}

              {/* Content */}
              <div style={{
                padding: '24px'
              }}>
                {loadingTakeAwayOrders ? (
                  <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 'calc(100vh - 200px)',
                    textAlign: 'center',
                    color: '#9ca3af' 
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      border: '4px solid #374151',
                      borderTop: '4px solid #ec4899',
                      borderRadius: '50%',
                      marginBottom: '16px',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <p>Loading orders...</p>
                  </div>
                ) : takeAwayOrders.length === 0 ? (
                  <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 'calc(100vh - 300px)',
                    textAlign: 'center',
                    padding: '40px',
                    color: '#9ca3af'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}></div>
                    <p style={{ fontSize: '16px', marginBottom: '8px' }}>No take away orders yet</p>
  
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {takeAwayOrders.map((order, index) => {
                      const isPaid = order.paymentStatus === 'paid';
                      // Generate ticket number - sequential from oldest to newest
                      const ticketNumber = `T${(index + 1).toString().padStart(3, '0')}`;
                      
                      return (
                        <div key={order.orderId} style={{
                          backgroundColor: '#111518',
                          borderRadius: '12px',
                          padding: '16px',
                          border: `2px solid ${isPaid ? '#25C35E' : '#F9C806'}`
                        }}>
                          {/* Order Header - Order ID and Status Button on same row */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '8px'
                          }}>
                            <div style={{
                              flex: 1,
                              textAlign: 'left'
                            }}>
                              <div style={{
                                fontSize: '18px',
                                fontWeight: '600',
                                color: 'white',
                                marginBottom: '12px',
                                fontFamily: 'monospace',
                                letterSpacing: '1px'
                              }}>
                                {ticketNumber}
                              </div>
                            </div>
                            {/* Status Badge moved to header row */}
                            <div style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              backgroundColor: isPaid ? '#25C35E' : '#F9C806',
                              color: isPaid ? '#fff' : '#111',
                              fontSize: '12px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap',
                              flexShrink: 0
                            }}>
                              {isPaid ? 'PAID' : 'PENDING'}
                            </div>
                          </div>

                          {/* Items Section */}
                          <div style={{
                            backgroundColor: '#27313A',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '12px'
                          }}>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: '500',
                              color: 'white',
                              marginBottom: '8px',
                              fontWeight: 'bold'
                            }}>
                              Items ({order.totalItems})
                            </div>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px'
                            }}>
                              {order.items.map((item, idx) => (
                                <div key={idx} style={{
                                  fontSize: '14px',
                                  color: 'white',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}>
                                  <span>{item.menu_item_name} x{item.quantity}</span>
                                  <span style={{ fontWeight: '600' }}>₹{((item.price || 0) * item.quantity).toFixed(0)}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Bottom Row: Time and Total */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div style={{
                              fontSize: '13px',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <Clock size={16} strokeWidth={2} />
                              <span>
                                {(() => {
                                  const utcDate = new Date(order.createdAt);
                                  const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000));
                                  const day = istDate.getDate();
                                  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                  const month = months[istDate.getMonth()];
                                  const hours = istDate.getHours();
                                  const minutes = String(istDate.getMinutes()).padStart(2, '0');
                                  const ampm = hours >= 12 ? 'pm' : 'am';
                                  const displayHours = hours % 12 || 12;
                                  return `${day} ${month}, ${displayHours}:${minutes} ${ampm}`;
                                })()}
                              </span>
                            </div>
                            <div style={{
                              fontSize: '18px',
                              fontWeight: '600',
                              color: isPaid ? '#25C35E' : '#F9C806'
                            }}>
                              ₹{order.totalAmount.toFixed(0)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Maps Link Modal */}
      {showGoogleMapsModal && (
        <div
          onClick={() => setShowGoogleMapsModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '0'
          }}>
          <div onClick={(e) => e.stopPropagation()} className="google-maps-modal-content" style={{
            backgroundColor: '#1A1A1A',
            borderRadius: '0.8rem',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}>
            {/* Header */}
            <div
              className="merchant-upi-modal-header"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1 }}>
                <div>
                  <h2 style={{
                    margin: 0,
                    fontSize: '1.125em',
                    fontWeight: '700',
                    color: '#f3f4f6',
                    textAlign: 'left'
                  }}>
                    Google Maps Review Link
                  </h2>
                </div>
              </div>
              <button
                onClick={() => setShowGoogleMapsModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#f3f4f6',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  padding: '4px'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Content */}
            <div className="merchant-upi-modal-body">
              <div style={{
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: '#0A0A0A',
                borderRadius: '8px'
              }}>
                <label style={{
                  display: 'block',
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#f3f4f6',
                  marginBottom: '8px',
                  textAlign: 'left'
                }}>
                  Google Maps Link
                </label>
                <input
                  className="gmaps-link-input"
                  type="url"
                  value={googleMapsLinkInput}
                  onChange={(e) => {
                    setGoogleMapsLinkInput(e.target.value);
                    setGoogleMapsLinkError('');
                  }}
                  placeholder="https://maps.app.goo.gl/..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#0a0a0a',
                    border: `2px solid ${googleMapsLinkError ? '#ef4444' : '#374151'}`,
                    borderRadius: '8px',
                    color: '#f3f4f6',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    textAlign: 'left'
                  }}
                  onFocus={(e) => {
                    if (!googleMapsLinkError) {
                      e.target.style.borderColor = '#3b82f6';
                    }
                  }}
                  onBlur={(e) => {
                    if (!googleMapsLinkError) {
                      e.target.style.borderColor = '#374151';
                    }
                  }}
                />
                {googleMapsLinkError && (
                  <p style={{
                    margin: '8px 0 0 0',
                    fontSize: '13px',
                    color: '#ef4444',
                    textAlign: 'left'
                  }}>
                    {googleMapsLinkError}
                  </p>
                )}
                <p style={{
                  margin: '8px 0 0 0',
                  fontSize: '0.75rem',
                  color: '#9ca3af',
                  textAlign: 'left'
                }}>
                  Paste your Google Maps review link here.<br/>Valid formats: maps.app.goo.gl, google.com/maps, g.page
                </p>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginTop: '24px'
              }}>
                <button
                  onClick={() => setShowGoogleMapsModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: 'rgb(55, 65, 81)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#f3f4f6',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={saveGoogleMapsLink}
                  disabled={loadingGoogleMapsLink || (googleMapsLinkInput && !isValidGoogleMapsLink(googleMapsLinkInput))}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: (loadingGoogleMapsLink || (googleMapsLinkInput && !isValidGoogleMapsLink(googleMapsLinkInput)))
                      ? '#6b7280'
                      : '#fbbf24',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#000000',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: (loadingGoogleMapsLink || (googleMapsLinkInput && !isValidGoogleMapsLink(googleMapsLinkInput)))
                      ? 'not-allowed'
                      : 'pointer',
                    opacity: (loadingGoogleMapsLink || (googleMapsLinkInput && !isValidGoogleMapsLink(googleMapsLinkInput))) ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!loadingGoogleMapsLink && (!googleMapsLinkInput || isValidGoogleMapsLink(googleMapsLinkInput))) {
                      e.currentTarget.style.backgroundColor = '#fbbf24';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loadingGoogleMapsLink && (!googleMapsLinkInput || isValidGoogleMapsLink(googleMapsLinkInput))) {
                      e.currentTarget.style.backgroundColor = '#fbbf24';
                    }
                  }}
                >
                  {loadingGoogleMapsLink ? 'Saving...' : 'Save Link'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Merchant UPI Link Modal */}
      {showMerchantUpiModal && (
        <div
          onClick={() => setShowMerchantUpiModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '0'
          }}>
          <div onClick={(e) => e.stopPropagation()} className="merchant-upi-modal-content" style={{
            backgroundColor: '#1A1A1A',
            borderRadius: '0.8rem',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}>
            {/* Header */}
            <div style={{
              padding: '1rem 0.5rem 0 0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1 }}>
                <div>
                  <h2 style={{
                    margin: 0,
                    fontSize: '1.125em',
                    fontWeight: '700',
                    color: '#f3f4f6',
                    textAlign: 'left'
                  }}>
                    Merchant UPI Link
                  </h2>
                </div>
              </div>
              <button
                onClick={() => setShowMerchantUpiModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#f3f4f6',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  padding: '4px'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Content */}
            <div className="merchant-upi-modal-body">
              {/* Toggle between auto-generate and manual link */}
              <div style={{
                marginBottom: '16px',
                display: 'flex',
                gap: '12px',
                borderRadius: '8px'
              }}>
                <button
                  onClick={() => setUseManualLink(false)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: !useManualLink ? '#fbbf24' : 'transparent',
                    border: !useManualLink ? 'none' : '1px solid #374151',
                    borderRadius: '6px',
                    color: !useManualLink ? '#000000' : '#f3f4f6',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Auto Generate
                </button>
                <button
                  onClick={() => setUseManualLink(true)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: useManualLink ? '#fbbf24' : 'transparent',
                    border: useManualLink ? 'none' : '1px solid #374151',
                    borderRadius: '6px',
                    color: useManualLink ? '#000000' : '#f3f4f6',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Manual Link
                </button>
              </div>

              {!useManualLink ? (
                /* Auto-generate mode with UPI ID and Merchant Name */
                <>
                  <div style={{
                    marginBottom: '16px',
                    padding: '16px',
                    backgroundColor: '#0A0A0A',
                    borderRadius: '8px'
                  }}>
                    <label style={{
                      display: 'block',
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#f3f4f6',
                      marginBottom: '8px',
                      textAlign: 'left'
                    }}>
                      UPI ID *
                    </label>
                    <input
                      type="text"
                      value={upiId}
                      onChange={(e) => {
                        setUpiId(e.target.value);
                        setMerchantUpiLinkError('');
                      }}
                      placeholder="merchant@upi (e.g., 9876543210@paytm)"
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#0a0a0a',
                        border: `2px solid ${merchantUpiLinkError ? '#ef4444' : '#374151'}`,
                        borderRadius: '8px',
                        color: '#f3f4f6',
                        fontSize: '0.875rem',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        textAlign: 'left'
                      }}
                      onFocus={(e) => {
                        if (!merchantUpiLinkError) {
                          e.target.style.borderColor = '#3b82f6';
                        }
                      }}
                      onBlur={(e) => {
                        if (!merchantUpiLinkError) {
                          e.target.style.borderColor = '#374151';
                        }
                      }}
                    />
                    <p style={{
                      margin: '8px 0 0 0',
                      fontSize: '0.75rem',
                      color: '#9ca3af',
                      textAlign: 'left'
                    }}>
                      Enter your UPI ID (e.g., 9876543210@paytm, merchant@phonepe)
                    </p>
                  </div>

                  <div style={{
                    marginBottom: '16px',
                    padding: '16px',
                    backgroundColor: '#0A0A0A',
                    borderRadius: '8px'
                  }}>
                    <label style={{
                      display: 'block',
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#f3f4f6',
                      marginBottom: '8px',
                      textAlign: 'left'
                    }}>
                      Merchant Name
                    </label>
                    <input
                      type="text"
                      value={merchantName}
                      onChange={(e) => {
                        setMerchantName(e.target.value);
                        setMerchantUpiLinkError('');
                      }}
                      placeholder="Your Business Name"
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#0a0a0a',
                        border: '2px solid #374151',
                        borderRadius: '8px',
                        color: '#f3f4f6',
                        fontSize: '0.875rem',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        textAlign: 'left'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#374151'}
                    />
                    <p style={{
                      margin: '8px 0 0 0',
                      fontSize: '0.75rem',
                      color: '#9ca3af',
                      textAlign: 'left'
                    }}>
                      This name will appear in the payment request
                    </p>
                  </div>

                  {/* Generated Link Preview */}
                  {upiId && (
                    <div style={{
                      marginBottom: '16px',
                      padding: '16px',
                      backgroundColor: '#0A0A0A',
                      borderRadius: '8px',
                      border: '1px solid #374151'
                    }}>
                      <label style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#9ca3af',
                        marginBottom: '8px',
                        textAlign: 'left'
                      }}>
                        Generated Link Preview:
                      </label>
                      <code style={{
                        display: 'block',
                        padding: '8px',
                        backgroundColor: '#000',
                        borderRadius: '4px',
                        color: '#3b82f6',
                        fontSize: '0.75rem',
                        wordBreak: 'break-all',
                        textAlign: 'left'
                      }}>
                        {generateUpiLink(upiId, merchantName)}
                      </code>
                    </div>
                  )}
                </>
              ) : (
                /* Manual link mode */
                <div style={{
                  marginBottom: '16px',
                  padding: '16px',
                  backgroundColor: '#0A0A0A',
                  borderRadius: '8px'
                }}>
                  <label style={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#f3f4f6',
                    marginBottom: '8px',
                    textAlign: 'left'
                  }}>
                    UPI Payment Link
                  </label>
                  <input
                    className="merchant-upi-link-input"
                    type="text"
                    value={merchantUpiLinkInput}
                    onChange={(e) => {
                      setMerchantUpiLinkInput(e.target.value);
                      setMerchantUpiLinkError('');
                    }}
                    placeholder="upi://pay?pa=merchant@upi&pn=MerchantName"
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#0a0a0a',
                      border: `2px solid ${merchantUpiLinkError ? '#ef4444' : '#374151'}`,
                      borderRadius: '8px',
                      color: '#f3f4f6',
                      fontSize: '0.875rem',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      textAlign: 'left'
                    }}
                    onFocus={(e) => {
                      if (!merchantUpiLinkError) {
                        e.target.style.borderColor = '#3b82f6';
                      }
                    }}
                    onBlur={(e) => {
                      if (!merchantUpiLinkError) {
                        e.target.style.borderColor = '#374151';
                      }
                    }}
                  />
                  <p style={{
                    margin: '8px 0 0 0',
                    fontSize: '0.75rem',
                    color: '#9ca3af',
                    textAlign: 'left'
                  }}>
                    Paste your UPI payment link here. Valid formats: upi://pay?pa=... or https://payment-link.com
                  </p>
                </div>
              )}

              {merchantUpiLinkError && (
                <p style={{
                  margin: '0 0 16px 0',
                  fontSize: '13px',
                  color: '#ef4444',
                  textAlign: 'left'
                }}>
                  {merchantUpiLinkError}
                </p>
              )}

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginTop: '24px'
              }}>
                <button
                  onClick={() => setShowMerchantUpiModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: 'rgb(55, 65, 81)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#f3f4f6',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                {merchantUpiLink && (
                  <button
                    onClick={deleteMerchantUpiLink}
                    disabled={loadingMerchantUpiLink}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: loadingMerchantUpiLink ? '#6b7280' : '#dc2626',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: loadingMerchantUpiLink ? 'not-allowed' : 'pointer',
                      opacity: loadingMerchantUpiLink ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!loadingMerchantUpiLink) {
                        e.currentTarget.style.backgroundColor = '#b91c1c';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loadingMerchantUpiLink) {
                        e.currentTarget.style.backgroundColor = '#dc2626';
                      }
                    }}
                  >
                    {loadingMerchantUpiLink ? 'Deleting...' : 'Remove Link'}
                  </button>
                )}
                <button
                  onClick={saveMerchantUpiLink}
                  disabled={loadingMerchantUpiLink || (merchantUpiLinkInput && !isValidMerchantUpiLink(merchantUpiLinkInput))}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: (loadingMerchantUpiLink || (merchantUpiLinkInput && !isValidMerchantUpiLink(merchantUpiLinkInput)))
                      ? '#6b7280'
                      : '#fbbf24',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#000000',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: (loadingMerchantUpiLink || (merchantUpiLinkInput && !isValidMerchantUpiLink(merchantUpiLinkInput)))
                      ? 'not-allowed'
                      : 'pointer',
                    opacity: (loadingMerchantUpiLink || (merchantUpiLinkInput && !isValidMerchantUpiLink(merchantUpiLinkInput))) ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!loadingMerchantUpiLink && (!merchantUpiLinkInput || isValidMerchantUpiLink(merchantUpiLinkInput))) {
                      e.currentTarget.style.backgroundColor = '#fbbf24';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loadingMerchantUpiLink && (!merchantUpiLinkInput || isValidMerchantUpiLink(merchantUpiLinkInput))) {
                      e.currentTarget.style.backgroundColor = '#fbbf24';
                    }
                  }}
                >
                  {loadingMerchantUpiLink ? 'Saving...' : 'Save Link'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Upload Modal */}
      {showVideoUploadModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setShowVideoUploadModal(false)}
        >
          <div onClick={e => e.stopPropagation()}>
            <BusinessVideoUpload
              businessId={businessId}
              onClose={() => setShowVideoUploadModal(false)}
            />
          </div>
        </div>
      )}

      {/* Status Modal (replaces browser alerts) */}
      {statusModal.open && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setStatusModal(prev => ({ ...prev, open: false }))}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '16px',
            backdropFilter: 'blur(4px)'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(420px, 92vw)',
              background: '#000000',
              borderRadius: '12px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
              border: '1px solid #6b7280',
              padding: '32px',
              textAlign: 'center',
              minWidth: '300px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                backgroundColor: '#000000',
                borderRadius: '50%',
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'scaleIn 0.4s ease-out'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  border: statusModal.type === 'success'
                    ? '3px solid #22c55e'
                    : statusModal.type === 'error'
                      ? '3px solid #ef4444'
                      : statusModal.type === 'warning'
                        ? '3px solid #f59e0b'
                        : '3px solid #3b82f6',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={statusModal.type === 'success'
                      ? '#22c55e'
                      : statusModal.type === 'error'
                        ? '#ef4444'
                        : statusModal.type === 'warning'
                          ? '#f59e0b'
                          : '#3b82f6'}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {statusModal.type === 'success' && <polyline points="20 6 9 17 4 12"></polyline>}
                    {statusModal.type === 'error' && <><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></>}
                    {statusModal.type === 'warning' && <><line x1="12" y1="9" x2="12" y2="13"></line><circle cx="12" cy="17" r="1"></circle></>}
                    {statusModal.type === 'info' && <><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></>}
                  </svg>
                </div>
              </div>
            </div>

            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#ffffff',
              marginBottom: '8px'
            }}>
              {statusModal.type === 'success' ? 'Success!' : statusModal.type === 'error' ? 'Error!' : statusModal.type === 'warning' ? 'Warning!' : 'Info'}
            </h3>

            <p style={{
              fontSize: '14px',
              color: '#9ca3af',
              marginBottom: 0,
              whiteSpace: 'pre-line',
              lineHeight: 1.5
            }}>
              {statusModal.message}
            </p>
          </div>
        </div>
      )}

      {/* Voice Selector Modal */}
      {showVoiceSelector && (
        <div
          onClick={() => setShowVoiceSelector(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '16px',
            backdropFilter: 'blur(4px)'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(600px, 92vw)',
              maxHeight: '80vh',
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
              borderRadius: '16px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              border: '2px solid #3b82f6',
              padding: '24px',
              overflowY: 'auto'
            }}
          >
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#fff', margin: 0 }}>
                🎤 Select Voice
              </h2>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                  onClick={reloadVoices}
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  title="Reload voices"
                >
                  🔄 Reload
                </button>
                <button
                  onClick={() => setShowVoiceSelector(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#9ca3af',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '0',
                    lineHeight: 1
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <p style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '20px' }}>
              Click "Test" to hear a sample announcement. Click "Select" to use that voice for all announcements.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {availableVoices.length === 0 ? (
                <div style={{
                  padding: '32px',
                  textAlign: 'center',
                  color: '#9ca3af',
                  fontSize: '14px'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                  Loading voices...
                  <div style={{ marginTop: '12px', fontSize: '12px' }}>
                    If voices don't appear, close and reopen this modal.
                  </div>
                </div>
              ) : (
                availableVoices.map((voice, index) => {
                  const isSelected = selectedVoiceName === voice.name;
                  const isTesting = testingVoice === voice.name;
                  const isEnglish = voice.lang.toLowerCase().includes('en');
                  
                  return (
                    <div
                      key={index}
                      style={{
                        background: isSelected ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : '#1a1a1a',
                        border: isSelected ? '2px solid #60a5fa' : '1px solid #374151',
                        borderRadius: '8px',
                        padding: '16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s',
                        opacity: isEnglish ? 1 : 0.6
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: '600', 
                          color: '#fff',
                          marginBottom: '4px'
                        }}>
                          {voice.name}
                          {isSelected && ' ✓'}
                          {isEnglish && ' 🌟'}
                        </div>
                        <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                          {voice.lang} • {voice.localService ? 'Local' : 'Cloud'}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => testVoiceWithSample(voice.name)}
                          disabled={isTesting}
                          style={{
                            background: isTesting ? '#6b7280' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: isTesting ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {isTesting ? '🔊 Playing...' : '▶️ Test'}
                        </button>
                        
                        {!isSelected && (
                          <button
                            onClick={() => selectVoice(voice.name)}
                            style={{
                              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Select
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div style={{
              marginTop: '20px',
              padding: '16px',
              background: '#0f172a',
              borderRadius: '8px',
              border: '1px solid #1e293b'
            }}>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>
                <strong style={{ color: '#fff' }}>Sample Sentence:</strong>
              </div>
              <div style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: 1.6 }}>
                "New order from table 5. Two Chicken Biryani and one Coke. Please confirm payment for table 3. Amount rupees 450."
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
                🌟 = English voice (recommended) • Total voices: {availableVoices.length}
              </div>
            </div>

            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button
                onClick={() => setShowVoiceSelector(false)}
                style={{
                  background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 32px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {showSuccessPopup && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#000000',
          borderRadius: '12px',
          padding: '32px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
          border: '1px solid #6b7280',
          zIndex: 10000,
          textAlign: 'center',
          minWidth: '300px',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#000000',
            borderRadius: '50%',
            margin: '0 auto 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'scaleIn 0.4s ease-out'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              border: '3px solid #22c55e',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#22c55e"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
          </div>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#ffffff',
            marginBottom: '8px'
          }}>
            Success!
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#9ca3af',
            marginBottom: 0
          }}>
            Waiter assignments saved successfully
          </p>
        </div>
      )}

      {/* Payment QR Code Upload Modal */}
      {showQrUploadModal && (
        <div
          onClick={() => setShowQrUploadModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '0'
          }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: '#1A1A1A',
            borderRadius: '0.8rem',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}>
            {/* Header */}
            <div style={{
              padding: '1rem 0.5rem 0 0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1 }}>
                <div>
                  <h2 style={{
                    margin: 0,
                    fontSize: '1.125em',
                    fontWeight: '700',
                    color: '#f3f4f6',
                    textAlign: 'left'
                  }}>
                    Upload Payment QR Code
                  </h2>
                </div>
              </div>
              <button
                onClick={() => setShowQrUploadModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#f3f4f6',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  padding: '4px'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '1rem' }}>
              {/* Info Box */}
              <div style={{
                backgroundColor: '#1f2937',
                border: '1px solid #374151',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px'
              }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#9ca3af', lineHeight: '1.5' }}>
                  <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '8px', color: '#fbbf24' }} />
                  Upload your pre-existing payment QR code image. This QR will be shown to customers and waiters during payment. Supported formats: JPG, PNG (max 5MB).
                </p>
              </div>

              {/* File Input */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#f3f4f6',
                  marginBottom: '8px'
                }}>
                  Select QR Code Image
                </label>
                <input
                  key={showQrUploadModal ? 'qr-input-open' : 'qr-input-closed'}
                  type="file"
                  accept="image/*"
                  onChange={handleQrImageSelect}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#f3f4f6',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Preview */}
              {paymentQrPreview && (
                <div style={{
                  marginBottom: '20px',
                  textAlign: 'center'
                }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#f3f4f6',
                    marginBottom: '8px'
                  }}>
                    Preview
                  </label>
                  <div style={{
                    display: 'inline-block',
                    padding: '10px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '8px'
                  }}>
                    <img
                      src={paymentQrPreview}
                      alt="Payment QR Preview"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Error Message */}
              {qrUploadError && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#7f1d1d',
                  border: '1px solid #991b1b',
                  borderRadius: '6px',
                  marginBottom: '20px'
                }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#fca5a5' }}>
                    {qrUploadError}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginTop: '20px'
              }}>
                <button
                  onClick={() => setShowQrUploadModal(false)}
                  disabled={loadingQrUpload}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: loadingQrUpload ? '#374151' : '#4b5563',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: loadingQrUpload ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Cancel
                </button>
                
                <button
                  onClick={uploadPaymentQrImage}
                  disabled={loadingQrUpload || !paymentQrImage || (typeof paymentQrImage !== 'object')}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: loadingQrUpload || !paymentQrImage || (typeof paymentQrImage !== 'object') ? '#374151' : '#fbbf24',
                    color: loadingQrUpload || !paymentQrImage || (typeof paymentQrImage !== 'object') ? '#9ca3af' : '#000000',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: loadingQrUpload || !paymentQrImage || (typeof paymentQrImage !== 'object') ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {loadingQrUpload ? 'Uploading...' : 'Upload QR Code'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Confirmation Modal */}
      {(() => {
        return showPaymentConfirmModal && pendingPaymentOrder;
      })() && (
        <div onClick={() => {
          // Clear voice announcement interval
          if (voiceAnnouncementIntervalRef.current) {
            clearInterval(voiceAnnouncementIntervalRef.current);
            voiceAnnouncementIntervalRef.current = null;
            window.speechSynthesis.cancel();
          }
          setShowPaymentConfirmModal(false);
        }} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(4px)'
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: '#0b0b0b',
            borderRadius: '16px',
            padding: '20px',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            border: '1px solid #374151',
            margin: '20px'
          }}>
            {/* Title */}
            <h2 style={{
              color: '#fff',
              fontSize: '24px',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '16px'
            }}>
              ⚠️ Verify Payment First
            </h2>

            {/* Message */}
            <p style={{
              color: '#FFD700',
              fontSize: '18px',
              textAlign: 'center',
              marginBottom: '8px',
              lineHeight: '1.5',
              fontWeight: '600'
            }}>
              IMPORTANT: Only confirm AFTER verifying actual payment!
            </p>
            
            <p style={{
              color: 'white',
              fontSize: '16px',
              textAlign: 'center',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              Go to table {pendingPaymentOrder.tableNumber} and ask customer to show UPI payment confirmation for ₹{pendingPaymentOrder.amount}
            </p>

            {/* Order Details */}
            <div style={{
              backgroundColor: '#262525',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ color: '#ffffff', fontSize: '16px', marginBottom: '8px' }}>Order Items:</div>
              {pendingPaymentOrder.items?.map((item, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: '#fff',
                  fontSize: '14px',
                  marginBottom: '4px'
                }}>
                  <span>{item.name} x{item.quantity}</span>
                  <span>₹{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  // Clear voice announcement interval
                  if (voiceAnnouncementIntervalRef.current) {
                    clearInterval(voiceAnnouncementIntervalRef.current);
                    voiceAnnouncementIntervalRef.current = null;
                    window.speechSynthesis.cancel();
                  }
                  // DON'T remove from pendingPayments - just close modal
                  // This allows the modal to reappear when table is clicked again
                  setShowPaymentConfirmModal(false);
                  setPendingPaymentOrder(null);
                }}
                style={{
                  padding: '14px 24px',
                  backgroundColor: 'rgb(38, 42, 48)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              <button
                onClick={async () => {
                  try {
                    // Clear voice announcement interval immediately
                    if (voiceAnnouncementIntervalRef.current) {
                      clearInterval(voiceAnnouncementIntervalRef.current);
                      voiceAnnouncementIntervalRef.current = null;
                      window.speechSynthesis.cancel();
                    }
                    
                    // ⚠️ CRITICAL: Add confirmation dialog to prevent accidental clicks
                    const userConfirmed = window.confirm(
                      `⚠️ IMPORTANT: Have you verified the customer's UPI payment?\n\n` +
                      `Before clicking OK:\n` +
                      `1. Go to Table ${pendingPaymentOrder.tableNumber}\n` +
                      `2. Ask customer to show UPI payment confirmation\n` +
                      `3. Verify payment of ₹${pendingPaymentOrder.amount}\n\n` +
                      `Only click OK if payment is CONFIRMED.`
                    );
                    
                    if (!userConfirmed) {
                      return;
                    }
                    
                    // ✅ Validate orderId exists before confirming payment
                    if (!pendingPaymentOrder.orderId) {
                      console.error('❌ Cannot confirm payment: orderId is missing');
                      showStatusModal('Error: Order ID not found. Please refresh and try again.', 'error', null);
                      setShowPaymentConfirmModal(false);
                      setPendingPaymentOrder(null);
                      loadDashboard(); // Refresh to get proper data
                      return;
                    }

                    // Confirm payment and send order to kitchen
                    const API_BASE = process.env.REACT_APP_API_URL;
                    const token = localStorage.getItem('token');

                    const response = await fetch(`${API_BASE}/qr-billing/confirm-payment`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        'X-Business-ID': businessId.toString()
                      },
                      body: JSON.stringify({
                        orderId: pendingPaymentOrder.orderId,
                        tableNumber: pendingPaymentOrder.tableNumber
                      })
                    });

                    if (response.ok) {
                      
                      // Remove from pendingPayments Map (no longer pending confirmation)
                      setPendingPayments(prev => {
                        const newMap = new Map(prev);
                        newMap.delete(String(pendingPaymentOrder.tableNumber));
                        return newMap;
                      });
                      
                      // Close modal and clear pending payment
                      setShowPaymentConfirmModal(false);
                      setPendingPaymentOrder(null);
                      
                      // Refresh dashboard to show updated status
                      // Order will appear in owner notifications if toggles are OFF
                      // Or go to chef/waiter if toggles are ON
                      loadDashboard();
                    } else {
                      const errorData = await response.json().catch(() => ({}));
                      console.error('❌ Payment confirmation failed:', response.status, errorData);
                      throw new Error(errorData.error || 'Failed to confirm payment');
                    }
                  } catch (error) {
                    console.error('❌ Error confirming payment:', error);
                    showStatusModal(`Payment confirmation failed: ${error.message}`, 'error', null);
                  }
                }}
                style={{
                  padding: '14px 24px',
                  backgroundColor: '#25C35E',
                  color: '#fff',
                  border: '1px solid #25C35E',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ✅ Confirm (Payment Verified)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add animation styles */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translate(-50%, -60%);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }
        
        @keyframes scaleIn {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }
        

      `}</style>
    </div>
  );
}